import axios from 'axios';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { imagePath, urls } from '../Url';
import storage from '../Assets/js/Global';
import { resetGuidelines } from "../utils/scanGuidelines";
import moment from 'moment';
import Loader from '../Components/Loader';
import useWhiteLabel from '../Assets/js/useWhiteLabel';
import { jwtDecode } from 'jwt-decode';
import { images } from '../Assets/images';
import { formatBiomarkerName, findNormalizedValue, normalizeScanDetails } from '../pages/scanDetails/scanNormalizer';
import { prefetchVoiceManifest } from '../utils/speech';

export const GlobalContext = createContext();

axios.defaults.withCredentials = true;

// The public campaign/invite guest flow (/invite/:code, /invite-language/:code,
// /invite/:code/guidelines, /invite/:code/face, /invite/:code/result,
// /invite/:code/all-results — NOT the
// admin /invites-* screens)
// keeps its own language selection under a separate storage key. Otherwise,
// since localStorage is shared across tabs of the same origin, a guest picking
// a language on an invite link open in one tab would silently change the
// language of the main site open in another tab.
const INVITE_LANGUAGE_STORAGE_KEY = 'inviteLanguageCode';
const isInviteGuestRoute = (pathname) =>
    pathname.startsWith('/invite-language/') || pathname.startsWith('/invite/');
const getLanguageStorageKey = () =>
    isInviteGuestRoute(window.location.pathname) ? INVITE_LANGUAGE_STORAGE_KEY : 'selectedLanguage';
// `appLabels` caches the translated-label payload for whichever language was
// last fetched. It must be split the same way as the language code above —
// otherwise fetching labels for the invite flow's language overwrites the
// same cache slot the main site reads on its next load (and vice versa),
// bleeding one flow's language into the other even though the language code
// itself is correctly isolated.
const getLabelsStorageKey = () =>
    isInviteGuestRoute(window.location.pathname) ? 'inviteAppLabels' : 'appLabels';

// Stamp every outgoing request with the current UI language so the backend can
// localize its response, without having to thread languageCode through each of
// the many individual API calls below by hand. Runs before per-call config, so a
// call that already sets its own languageCode keeps its own value.
axios.interceptors.request.use((config) => {
    const languageCode = storage.get(getLanguageStorageKey()) || 'en';

    if (config.data instanceof FormData) {
        if (!config.data.has('languageCode')) {
            config.data.append('languageCode', languageCode);
        }
    } else if (config.method?.toLowerCase() === 'get') {
        config.params = { languageCode, ...(config.params || {}) };
    } else if (
        !config.data ||
        (typeof config.data === 'object' && !(config.data instanceof URLSearchParams))
    ) {
        config.data = { languageCode, ...(config.data || {}) };
    }

    return config;
});

// A campaign guest has no account, so their completed scan is kept per invite
// in localStorage. Reopening the link makes check/campaign answer "Already
// participated" — this cache is then the only way to show them their result.
const campaignResultKey = (campaignCode) => `campaignResult_${campaignCode}`;

// check/campaign flags a repeat visit with `is_participated: true` on the
// response body — read that boolean directly rather than pattern-matching
// whatever wording happens to be in msg/error (the backend has used more than
// one phrasing for the same thing: "already participated", "already been
// participated in", "already been accepted").
export const isAlreadyParticipated = (data) => Boolean(data?.is_participated);

// navigator.platform reports the underlying kernel/arch (e.g. "Linux aarch64"
// on Android), not a friendly OS name, so derive it from the user agent instead.
const getOSName = () => {
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'Android';
    if (/iPad|iPhone|iPod/i.test(ua)) return 'iOS';
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return navigator.platform || 'Unknown';
};

export const GlobalProvider = ({ children }) => {

    const location = useLocation();
    let loginObject = storage.get('loginObj');
    let deviceInfo = storage.get('device_info');
    const userId = loginObject?.userKey;
    let emailStored = storage.get('verifiedEmail');
    let userModule = storage.get('UserModule');
    const currentPath = location.pathname;
    let companyId = process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey;
    const userEmail = loginObject?.userEmail;
    const isLoggedIn = storage.get('isLoggedIn');
    const licenseType = storage.get('licenseType') || 'user';
    const refreshTimerRef = useRef(null);
    const is4Life = process.env.REACT_APP_COMPANY_ID === '0it02f';
    const [redirection, setRedirection] = useState({
        productLink: "",
        productShare: ""
    });

    const navigate = useNavigate();
    // Loading state
    const [isLoading, setIsLoading] = useState(false);
    const [isInitLoading, setIsInitLoading] = useState(true);
    const [msgLoader, setMsgLoader] = useState(false);
    const [timeZone, setTimeZone] = useState([]);
    const [globalUserEmail, setGlobalUserEmail] = useState(userEmail);

    const [showPasswords, setShowPasswords] = useState({
        field1: false,
        field2: false,
    });
    const [userData, setUserData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        otp: '',
        image: '',
        setPassword: '',
        confirmPassword: ''
    });
    const [userDatav2, setUserDatav2] = useState({
        email: '',
        firstName: '',
        lastName: '',
        image: '',
    });

    const [signupData, setSignupData] = useState(() => {
        const saved = storage.get('signupData');
        return saved || {
            firstName: '',
            lastName: '',
            email: '',
            secondaryEmail: '',
            phoneNumber: '',
            accessCode: '',
            age: '',
            weight: '',
            weightUnit: 'kg',
            height: '',
            heightUnit: 'cm',
            race: '',
            gender: '',
            waistCircumference: '',
            waistUnit: 'inch',
            diabetes: '',
            hypertension: '',
            smoking: '',
            country: '',
            time_zone: '',
            wellnessCategory: [],
            fruits: '',
            vegetables: '',
            grains: '',
            sugaryFoods: '',
            redMeat: '',
            fish: '',
            activity: '',
            password: '',
            confirmPassword: ''
        };
    });

    const verifiedsecEmail = loginObject?.userSecondaryEmail || storage.get('verifiedSecondaryEmail');
    let [email, setEmail] = useState('');
    const [faqList, setFaqList] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [timer, setTimer] = useState(0);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [isThankYou, setIsThankYou] = useState(false);
    const [translatedLabels, setTranslatedLabels] = useState({});
    const [scanIssueModal, setScanIssueModal] = useState(null);
    const [deviceId, setDeviceId] = useState(() => storage.get("deviceId"));
    const [selected, setSelected] = useState(() => storage.get(getLanguageStorageKey()) || 'en');
    const [selectedBillType, setSelectedBillype] = useState(() => {
        const saved = storage.get("selectedBillingType");
        return saved ? Number(saved) : null;
    });
    const [userDetails, setUserDetails] = useState({});
    const [servings, setServings] = useState([]);
    const [isVerified, setIsVerified] = useState(false);
    const [logoUrl, setLogoUrl] = useState('');
    const [headerLogoUrl, setHeaderLogoUrl] = useState('');
    const [isManualChange, setIsManualChange] = useState(false);
    const [showOTPSection, setShowOTPSection] = useState(false);
    const [fourLifeScanHistory, setFourLifeScanHistory] = useState([]);
    const [documentData, setDocumentData] = useState({
        file: null,
        previewUrl: "",
        selectedCategory: "",
        documentID: null,
    });
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [documentContent, setDocumentContent] = useState({
        terms: '',
        privacy: '',
    });
    const [articlesList, setArticlesList] = useState([]);
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1,
        perPage: 10
    });
    const [search, setSearch] = useState("");
    const prevSearchRef = useRef("");
    const [articleDetails, setArticleDetails] = useState({});
    const [scanDetails, setScanDetails] = useState({});
    const [topSection, setTopSection] = useState(null);
    const [scanSpectrum, setScanSpectrum] = useState({});
    const [scanSpectrumV2, setScanSpectrumV2] = useState({});
    const [scanHistory, setScanHistory] = useState({})
    const [wellnessCat, setWellnessCat] = useState([])
    const [bioMarker, setBioMarker] = useState([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef(null);
    const [tempScanDetails, setTempScanDetails] = useState();
    const oauthPopupRef = useRef(null);
    const oauthSuccessTokenRef = useRef(null);
    const [historyDates, setHistoryDates] = useState([]);
    const [notification, setNotification] = useState([]);
    const [wellnessLabel, setWellnessLabel] = useState("");
    const [wellnessLabelNormal, setWellnessLabelNormal] = useState("");
    const [hasNotification, setHasNotification] = useState(false);
    const [isNotificationReceived, setIsNotificationReceived] = useState();
    const [notificationsEnabled, setNotificationsEnabled] = useState();
    const [isPlanActive, setIsPlanActive] = useState(storage.get('isPlanActive') || false);
    const [guestList, setGuestList] = useState({});
    const [scanLimit, setScanLimit] = useState(true);
    // Invitation/campaign quota — populated by getCampaignList from the
    // listCampaign API response (total_limit / used / remaining). `loaded`
    // lets screens tell "no quota yet fetched" apart from a real zero.
    const [inviteStats, setInviteStats] = useState({
        total: 0,
        used: 0,
        remaining: 0,
        guestScanCount: 0,
        inviteScanCount: 0,
        loaded: false,
    });
    const [fourLifeuserData, setFourLifeUserData] = useState({
        userName: "",
        password: "",
    });
    // Biosense SDK State
    const [isBiosenseInitialized, setIsBiosenseInitialized] = useState(false);
    const [biosenseSession, setBiosenseSession] = useState(null);
    const [biosenseState, setBiosenseState] = useState(null);
    const [isProfileUpdated, setIsProfileUpdated] = useState(false);
    const [products, setProducts] = useState({
        top4: [],
        remaining: [],
        shareLink: "https://example.com/recommendations/123",
    });
    const [dynamicTypes, setDynamicTypes] = useState({});
    const [fullProducts, setFullProducts] = useState({
        scanId: "",
        fullList: []
    })

    const bioLicenseKey = process.env.REACT_APP_BIOSENSE_LICENSE_KEY || "";

    const prevLangRef = useRef(selected);

    useEffect(() => {
        storage.set(getLanguageStorageKey(), selected)
    }, [selected])

    // Guards the label-version check so overlapping route changes don't fire
    // multiple concurrent version fetches / label refetches.
    const labelVersionCheckRef = useRef(false);

    useEffect(() => {
        const handleOAuthMessage = async (event) => {
            const message = event?.data;
            if (!message || typeof message !== 'object') return;
            if (message?.type !== 'OAUTH_SUCCESS') return;
            if (!message?.token) {
                window.showToast?.('error', screenLabels?.oauth_token_missing?.label_value || 'OAuth token missing from popup');
                return;
            }

            const payload = {
                deviceID: deviceId || storage.get('DeviceId'),
                token: message.token,
                companyKey: companyId
            };

            oauthSuccessTokenRef.current = message.token;

            try {
                const webhookRes = await axios.post(urls.webhookSignup, payload);
                const res = webhookRes?.data;
                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;
                    const isBack = res?.is_back;
                    storage.set('isBack', isBack ? true : false);

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);

                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res?.email);
                    }

                    if (next === "dashboard") {
                        window.location.href = is4Life ? '/dashboard2' : '/dashboard';
                    } else if (next === "signup") {
                        setEmail(res?.email);
                        storage.set('verifiedEmail', res?.email);
                        updateSignupData("email", res?.email || '');
                        updateSignupData("firstName", res?.first_name || '');
                        updateSignupData("lastName", res?.last_name || '');
                        // navigate('/signup/account-details');
                        isBack ? navigate('/welcome-back') : navigate('/signup/account-details');
                    } else {
                        window.location.href = '/login';
                    }

                } else {
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            } catch (err) {
                window.showToast('error', err?.response?.data?.error || err.message || screenLabels?.webhook_signup_failed?.label_value || 'Webhook signup failed');
            } finally {
                if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
                    oauthPopupRef.current.close();
                }
                oauthPopupRef.current = null;
            }
        };

        window.addEventListener('message', handleOAuthMessage);
        return () => {
            window.removeEventListener('message', handleOAuthMessage);
        };
    }, [deviceId,]);

    const screenLabels = translatedLabels?.common;
    useEffect(() => {
        setWellnessLabel(screenLabels?.avg_reading?.label_value || "Avg. Reading");
        // if (licenseType === "per_user_per_month") {
        // } else if (licenseType === "per_scan_per_month") {
        setWellnessLabelNormal(screenLabels?.normal_reading?.label_value || "Normal Reading");
        // }
    }, [screenLabels]);

    useEffect(() => {
        if (storage.get("isLoggedIn")) {
            storage.remove("signupData");
            storage.remove("signupStep")
            const loginObj = storage.get("loginObj");
            let userId = loginObj?.userKey;
            window.ReactNativeWebView?.postMessage(`userID|${userId}`);
        }
    }, [])

    useEffect(() => {
        function handleClickOutside(event) {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        }

        if (showCalendar) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showCalendar]);

    // Fire-and-forget: reports the outcome of a scan attempt (success or
    // cancelled/failed) to the backend. `overrides` lets a caller replace the
    // storage-derived identity fields — the invite/campaign flow has no
    // logged-in user and passes inviteUserKey/scanType directly instead.
    // Deliberately bypasses handleApiCall so it never triggers the global
    // loading spinner or an error toast for a background log call.
    const logScanAttempt = useCallback((scanStatus, msg, overrides = {}) => {
        let loginObject = storage.get('loginObj');
        let userId = loginObject?.userKey || "";
        let isGuest = storage.get("scanGuest") || false;
        let guestUserKey = storage.get("guestUserKey") || "";
        const freshToken = storage.get('accessToken');
        const payload = {
            userKey: userId,
            guestUserKey: isGuest ? guestUserKey : "",
            inviteUserKey: "",
            scanType: isGuest ? "guest" : "user",
            platform: "web",
            scanStatus,
            msg,
            ...overrides,
        };
        axios
            .post(
                urls.logScanCancelled,
                payload,
                freshToken ? { headers: { Authorization: `Bearer ${freshToken}` } } : {}
            )
            .catch((error) => console.error("❌ Failed to log scan attempt", error));
    }, []);

    // After a failed/cancelled scan, guest scans go back to the guest-scan
    // form with the draft saved by GuestScan restored (and the guest key just
    // created by saveGuestUser, so a retry updates that guest instead of
    // creating a duplicate). Regular scans just go back.
    const navigateAfterScanFail = useCallback(() => {
        if (storage.get("scanGuest") && storage.get("guestScanDraft")) {
            const guestUserKey = storage.get("guestUserKey");
            navigate('/guest-scan', {
                replace: true,
                state: {
                    restoreDraft: true,
                    guestData: guestUserKey ? { guest_user_key: guestUserKey } : undefined,
                },
            });
        } else {
            navigate(-1);
        }
    }, [navigate]);

    const startScan = useCallback(
        async (scanData) => {
            let deviceId = storage.get('DeviceId');
            let loginObject = storage.get('loginObj')
            let userId = loginObject?.userKey;
            let companyId = process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey;
            if (!deviceId || !scanData || !userId) return;
            let guestUserKey = storage.get("guestUserKey") || null;
            let isGuest = storage.get("scanGuest") || false;
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                scanData: scanData,
                guestUserKey: guestUserKey,
                scanType: isGuest ? "guest" : "user",
            };
            await handleApiCall(
                (config) => axios.post(urls.startScan, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        window.showToast('success', screenLabels?.scan_success?.label_value || "Scan completed successfully");
                        logScanAttempt("success", "scan success");
                        if (isGuest) {
                            storage.remove("guestScanDraft");
                            localStorage.setItem("scanId", res?.data?.scanKey);
                            navigate(is4Life ? '/user-scan-details-four-life' : '/user-scan-details');
                        } else {
                            navigate(is4Life ? '/dashboard2' : '/dashboard');
                        }
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                        logScanAttempt("fail", res?.error || "Something went wrong");
                        navigateAfterScanFail();
                    }
                },
                (error) => {
                    window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    logScanAttempt("fail", error?.response?.data?.error || error.message || "Something went wrong");
                },
                true
            );
        },
        [navigate, logScanAttempt, navigateAfterScanFail]
    );

    useEffect(() => {
        const handler = (event) => {
            try {
                let msg = event?.data;
                if (typeof msg === "string") {
                    try {
                        msg = JSON.parse(msg);
                    } catch (e) {
                        return;
                    }
                }
                if (msg && msg?.type === "finalValues") {
                    startScan(msg?.data);
                    setMsgLoader(false);
                } else if (msg?.type === "scanTimeout") {
                    setMsgLoader(false);
                    logScanAttempt("fail", msg?.reason || "noFaceDetected");
                    navigate(-1);
                    setScanIssueModal("timeout");
                }
                else if (msg?.type === "stopFaceScan") {
                    setMsgLoader(false);
                    logScanAttempt("fail", msg?.reason || "faceStopped");
                    navigate(-1);
                }
                else if (msg?.type === "lowQualityScan") {
                    // Not logged here — this always fires paired with a
                    // "stopFaceScan" message carrying the same reason, which
                    // already logs the attempt above.
                    setMsgLoader(false);
                    navigate(-1);
                    setScanIssueModal(msg?.reason === "blinkNotDetected" ? "blinkNotDetected" : "lowQuality");
                }
                else if (msg?.type === "cameraPermission") {
                    setMsgLoader(false);
                    navigate(is4Life ? "/dashboard2" : "/dashboard");
                    window.showToast("error", screenLabels?.camera_permission?.label_value || "Please allow camera access to use this feature.");
                }
            } catch (err) {
                console.error("❌ Failed to parse message", err, event?.data);
            } finally {

            }
        };

        document.addEventListener("message", handler);
        window.addEventListener("message", handler);

        return () => {
            document.removeEventListener("message", handler);
            window.removeEventListener("message", handler);
        };
    }, [startScan, navigate, logScanAttempt]);


    useEffect(() => {
        const handler = (event) => {
            try {
                let msg = event?.data;
                if (typeof msg === "string") {
                    try {
                        msg = JSON.parse(msg);
                    } catch (e) {
                        return;
                    }
                }

                if (msg?.msg === "licence" && msg?.type) {
                    let message = msg?.type == "per_scan_per_month" ? "user" : "session";
                    storage.set("licenseType", message);
                }

                if (msg?.msg === "notification" && msg?.type === "reminder") {
                    setHasNotification(true);
                }
            } catch (err) {
                console.error("❌ Failed to parse licence message", err, event?.data);
            }
        };

        document.addEventListener("message", handler);
        window.addEventListener("message", handler);

        return () => {
            document.removeEventListener("message", handler);
            window.removeEventListener("message", handler);
        };
    }, []);

    const handleApiCall = async (apiCall, successCallback, errorCallback, requiresAuth = true) => {
        setIsLoading(true);
        try {
            const freshToken = storage.get('accessToken');
            const config = requiresAuth ? {
                headers: { Authorization: `Bearer ${freshToken}` }
            } : {};
            const response = await apiCall(config);
            const result = successCallback ? successCallback(response) : undefined;
            return result !== undefined ? result : response;
            // if (successCallback) successCallback(response);
            // return response;
        } catch (error) {
            window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            if (error.status === 401) {
                getRefreshToken().then((newToken) => {
                    // Handle token refresh
                });
                return null;
            }
            const backendError = error?.response?.data?.error || error?.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong";
            if (errorCallback) {
                errorCallback(error);
            } else {
                window.showToast('error', backendError);
            }
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const togglePassword = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const updateUserData = (field, value) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        setUserData({
            // Keep the pre-login email so Login stays prefilled after a reload.
            email: storage.get('verifiedEmail') || '',
            password: '',
            firstName: '',
            lastName: '',
            otp: '',
            image: '',
            setPassword: '',
            confirmPassword: ''
        });
    }, []);

    function getFontScale() {
        return window.devicePixelRatio || 1;
    }

    function getTimezoneDifference() {
        const offset = new Date().getTimezoneOffset();
        const hours = Math.floor(Math.abs(offset) / 60);
        const minutes = Math.abs(offset) % 60;
        const sign = offset <= 0 ? '+' : '-';
        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    const getDeviceId = useCallback(async () => {
        try {
            const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

            const payload = {
                os: getOSName(),
                device_name: navigator.userAgentData?.brands?.[0]?.brand ?? 'Google Chrome',
                system_version: navigator.userAgent,
                font_scale: getFontScale(),
                brand: 'Unknown',
                model: 'Unknown',
                screen_size: `${window.screen.width}x${window.screen.height}`,
                cpu: navigator.hardwareConcurrency || 'Unknown',

                ram: navigator.deviceMemory
                    ? navigator.deviceMemory >= 8
                        ? `${navigator.deviceMemory}+ GB`
                        : `${navigator.deviceMemory} GB`
                    : 'Unknown',

                network_type: connection?.effectiveType || 'Unknown',
                downlink: connection ? `${connection.downlink} Mbps` : 'Unknown',
                network_rtt: connection ? `${connection.rtt} ms` : 'Unknown',
                device_tz: getTimezoneDifference(),
            };
            return await handleApiCall(
                () => axios.post(urls.getDeviceId, payload),
                (response) => {
                    if (response.data.status === true) {
                        storage.set('DeviceId', response.data.deviceID);
                        setDeviceId(response.data.deviceID);
                        return response.data.deviceID;
                    } else {
                        console.error('API Error:', response.data);
                        return null;
                    }
                },
                (error) => {
                    window.showToast('error', error);
                    return null;
                },
                false
            );
        } catch (error) {
            window.showToast('error', error);
        }
    }, []);

    const getCompanyBranding = useCallback(async (customDeviceId) => {
        const id = customDeviceId || deviceId || storage.get('DeviceId');
        if (!id) return;

        // Skip the getCompanyDetails network call if branding is already cached
        // (e.g. on a page refresh). Logout/delete flows clear storage first, so
        // they still fetch fresh branding.
        const existingBranding = storage.get('CompanyBranding');
        if (existingBranding) return existingBranding;

        const payload = {
            deviceID: id,
            companyKey: companyId
        };

        return await handleApiCall(
            (config) => axios.post(urls.getCompanyDetails, payload, { ...config, skipLogoutInterceptor: true }),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    storage.set("CompanyBranding", res.list);
                    return res.list;
                }
                return null;
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                return null;
            },
            false
        );

    }, []);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                let id = storage.get('DeviceId');
                if (!id) {
                    id = await getDeviceId();
                } else {
                    setDeviceId(id);
                }

                // Public campaign scan routes don't need company branding.
                const path = window.location.pathname;
                const isCampaignRoute =
                    path.startsWith('/invite/') ||
                    path.startsWith('/invite-language/');
                if (id && !isCampaignRoute) {
                    await getCompanyBranding(id);
                }
            } catch (err) {
                console.error("App initialization failed", err);
            } finally {
                setIsInitLoading(false);
            }
        };

        initializeApp();
    }, []);

    // NOTE: previously a beforeunload handler removed "CompanyBranding" from
    // storage on every unload, which forced getCompanyDetails to refetch on each
    // refresh. Branding now persists across refreshes (and is refreshed on
    // logout/login, which clear storage), so the call no longer fires on refresh.

    const checkUserRegistration = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!deviceId) return;
        if (!userData?.email) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return;
        }

        if (!emailRegex.test(userData?.email)) {
            window.showToast('error', screenLabels?.valid_email_required?.label_value || 'Please enter a valid email address');
            return;
        }
        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            email: userData?.email?.toLowerCase()
        };

        setEmail(userData?.email);

        await handleApiCall(
            () => axios.post(urls.checkUserRegistration, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    storage.set('UserModule', res.module);
                    storage.set('verifiedEmail', userData?.email);
                    if (res.module === 'Signup') {
                        navigate('/verify-otp');
                    } else if (res.module === 'Login') {
                        navigate('/password')
                    }
                    updateUserData('email', '')
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    updateUserData('email', '')
                }
            },
            (error) => {
                updateUserData('email', '')
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            },
            false
        );
    };

    const fourLifeSSOLogin = async () => {
        const response = await handleApiCall(
            () => axios.post(urls.ssoLogin, {}),
            (response) => response,
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.sso_login_failed?.label_value || "SSO login failed");
                return null;
            },
            false
        );

        const res = response?.data;
        if (!res || res.status !== true || !res.url) {
            window.showToast('error', res?.error || screenLabels?.sso_login_failed?.label_value || "SSO login failed");
            return;
        }

        const popup = window.open(res.url, 'oauthPopup', 'width=600,height=700,toolbar=0,menubar=0,location=1,resizable=1,scrollbars=1,status=1');
        if (!popup) {
            window.showToast('error', screenLabels?.popup_blocked?.label_value || 'Popup blocked. Please allow popups and try again.');
            return;
        }

        // oauthPopupRef.current = popup;
        // storage.set("secret", res.secret || '');

        // const closeWatcher = setInterval(async () => {
        //     if (popup.closed) {
        //         clearInterval(closeWatcher);
        //         oauthPopupRef.current = null;
        //         window.showToast('info', 'SSO window closed.');

        //         const secret = storage.get('secret') || '';
        //         if (!secret) {
        //             window.showToast('error', 'SSO secret token missing after close.');
        //             return;
        //         }

        //         try {
        //             const payload = {
        //                 deviceID: deviceId || storage.get('DeviceId'),
        //                 secret,
        //                 companyKey: companyId
        //             };
        //             const webhookRes = await axios.post(urls.webhookSignup, payload);
        //             const webhookData = webhookRes?.data;

        //             if (webhookData?.status === true) {
        //                 if (webhookData?.msg) window.showToast('success', webhookData.msg);

        //                 const auth = webhookData.auth;
        //                 const next = webhookData.next;

        //                 if (auth && typeof auth === 'object' && auth.access_token) {
        //                     const decodeAccessToken = jwtDecode(auth.access_token);
        //                     storage.set('accessToken', auth.access_token);
        //                     storage.set('refreshToken', auth.refresh_token);
        //                     storage.set('tokenExpiry', decodeAccessToken?.exp);
        //                     storage.set('loginObj', decodeAccessToken);
        //                     storage.set('isLoggedIn', true);
        //                     window.ReactNativeWebView?.postMessage(`userID|${decodeAccessToken?.userKey}`);
        //                     startTokenRefreshTimer();
        //                 }

        //                 if (next === 'dashboard') {
        //                     window.location.href = is4Life ? '/dashboard2' : '/dashboard';
        //                 } else if (next === 'signup') {
        //                     setEmail(webhookData?.email);
        //                     storage.set('verifiedEmail', webhookData?.email);
        //                     updateSignupData('email', webhookData?.email || '');
        //                     updateSignupData('firstName', webhookData?.first_name || '');
        //                     updateSignupData('lastName', webhookData?.last_name || '');
        //                     navigate('/signup/account-details');
        //                 } else {
        //                     window.location.href = '/login';
        //                 }
        //             } else {
        //                 window.showToast('error', webhookData?.error || 'Webhook signup failed after SSO close.');
        //             }
        //         } catch (err) {
        //             window.showToast('error', err?.response?.data?.error || err.message || 'Webhook call on popup close failed.');
        //         }
        //     }
        // }, 500);
    };

    const getuserDetails = async (deviceId, email) => {
        const payload = {
            deviceID: deviceId,
            email: email
        };

        setEmail(email);

        await handleApiCall(
            () => axios.post(urls.getuserDetails, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    setUserDatav2({
                        email: res.userDetails.email || '',
                        firstName: res.userDetails.first_name || '',
                        lastName: res.userDetails.last_name || '',
                        image: res.userDetails.profile_photo || '',
                    });

                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    updateUserData('email', '')
                }
            },
            (error) => {
                updateUserData('email', '')
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            },
            false
        );
    };

    const loginByPassword = async () => {
        if (!deviceId) return;
        if (!userData?.password) {
            window.showToast('error', screenLabels?.password_required?.label_value || 'Password is required');
            return;
        }
        const payload = {
            deviceID: deviceId,
            email: emailStored.toLowerCase(),
            password: userData?.password,
            licence_type: licenseType,
            company_id: process.env.REACT_APP_COMPANY_ID,
            device_data: deviceInfo
        };

        await handleApiCall(
            () => axios.post(urls.userLogin, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    storage.set('isLoggedIn', true)
                    const decodeAccessToken = jwtDecode(res?.token?.access_token);

                    storage.set('accessToken', res?.token?.access_token);
                    storage.set('refreshToken', res?.token?.refresh_token);
                    storage.set('tokenExpiry', decodeAccessToken?.exp);
                    startTokenRefreshTimer();
                    storage.set('loginObj', decodeAccessToken)
                    storage.remove('verifiedEmail');

                    const requestCamera = async () => {
                        try {
                            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                            stream.getTracks().forEach(track => track.stop());
                        } catch (err) {
                            console.warn("Camera permission not granted at login:", err);
                        }
                    }
                    requestCamera();
                    window.location.href = is4Life ? '/dashboard2' : '/dashboard';
                    storage.set('UserModule', 'Login')
                    let userId = decodeAccessToken?.userKey;
                    window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                    updateUserData('password', '')
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    updateUserData('password', '')
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                updateUserData('password', '')
            },
            false
        );
    };

    const sendEmailOTP = async () => {
        if (!deviceId) return;
        if (!email && !loginObject?.userEmail && !emailStored) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase() || loginObject?.userEmail?.toLowerCase() || emailStored.toLowerCase(),
            module: userModule,
            companyKey: companyId,
            role: 3
        };
        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    storage.set('verifiedEmail', email);
                    window.showToast('success', res.msg);
                    setShowOTPSection(true)
                    if (currentPath === '/change-password') {
                        navigate('/change-password')
                    } else if (storage.get("isLoggedIn") ? navigate('/deleteAcc-one') : storage.get('UserModule') == 'deleteUser' ? setShowOTPSection(true) : navigate('/verify-otp'))
                        email = '';
                    updateUserData('otp', '');
                    return true;
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    email = '';
                    updateUserData('otp', '');
                    return false;
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                email = '';
                return false;
            },
            isLoggedIn ? true : false
        );
    };

    const fourLifeEmailOTP = async ({ email, module }) => {
        if (!deviceId) return;

        if (!email) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase(),
            module: module,
            companyKey: companyId,
            role: loginObject?.role || 3
        };
        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    storage.set('verifiedSecondaryEmail', email);
                    window.showToast('success', res.msg);

                    updateUserData('otp', '');
                    return true;
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    email = '';
                    updateUserData('otp', '');
                    return false;
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                email = '';
                return false;
            },
            isLoggedIn ? true : false
        );
    };

    const resendEmailOTP = async () => {
        if (!deviceId) return;
        if (!email && !loginObject?.userEmail && !emailStored) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            deviceID: deviceId,
            email: email?.toLowerCase() || loginObject?.userEmail?.toLowerCase() || emailStored?.toLowerCase(),
            module: userModule,
            role: 3,
            companyKey: process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey,
        };

        storage.set('verifiedEmail', email);

        await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', screenLabels?.otp_resent_successfully?.label_value || 'OTP Resent successfully.');
                    startResendTimer();
                    updateUserData('otp', '');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    updateUserData('otp', '');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                updateUserData('otp', '');
            },
            isLoggedIn ? true : false
        );
    };

    const fourLiferesendOTP = async ({ email, module }) => {
        if (!deviceId) return;

        if (!email) {
            window.showToast('error', screenLabels?.email_required?.label_value || 'Email is required');
            return false;
        }
        const payload = {
            companyKey: process.env.REACT_APP_COMPANY_ID || loginObject?.companyKey,
            deviceID: deviceId,
            email: email?.toLowerCase(),
            module: module,
            role: loginObject?.role || 3
        };

        storage.set('verifiedSecondaryEmail', email);

        await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.sendEmailOTP, payload, config) : () => axios.post(urls.sendEmailOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', screenLabels?.otp_resent_successfully?.label_value || 'OTP Resent successfully.');
                    startResendTimer();
                    updateUserData('otp', '');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    updateUserData('otp', '');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                updateUserData('otp', '');
            },
            isLoggedIn ? true : false
        );
    };

    const startResendTimer = () => {
        setTimer(60);
        setResendDisabled(true);
    };

    const handlenavigateToEmail = () => {
        storage.set('UserModule', 'ForgetPassword');
        navigate('/forgot-password');
    };

    const handleChangePassword = () => {
        storage.set('UserModule', 'changePassword');
        navigate('/change-password');
    }

    const handleDeletAccount = () => {
        storage.set('UserModule', 'deleteUser');
        navigate('/deleteAcc-one');
    }
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
        } else {
            setResendDisabled(false);
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const verifyOTP = async () => {
        if (!deviceId) return;
        if (!userData?.otp) {
            window.showToast('error', screenLabels?.otp_required?.label_value || 'OTP is required');
            return;
        }
        const payload = {
            deviceID: deviceId,
            email: emailStored || userEmail || email,
            otp: userData?.otp,
            module: userModule,
            role: 3,
            companyKey: companyId
        };

        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.verifyOTP, payload, config) : () => axios.post(urls.verifyOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setSignupData({});
                    storage.remove('signupData')
                    setIsVerified(true)
                    window.showToast('success', res.msg);
                    if (currentPath === '/change-password') {
                        navigate('/change-password')
                    } else if (storage.get("isLoggedIn") ? navigate('/deleteAcc-one') : storage.get('UserModule') == 'deleteUser' ? setShowOTPSection(false) : navigate('/reset-password'));

                    if (userModule === "Signup") {
                        navigate('/signup/account-details');
                    } else {
                        getuserDetails(payload.deviceID, payload.email)
                    }
                    updateUserData('otp', '');
                    return true;
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('otp', '');
                    return false;
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('otp', '');
                return false;
            },
            isLoggedIn ? true : false
        );
    };

    const fourLifeVerifyOTP = async ({ secondaryemail, module }) => {
        if (!deviceId) return;
        if (!userData?.otp) {
            window.showToast('error', screenLabels?.otp_required?.label_value || 'OTP is required');
            return;
        }
        const payload = {
            deviceID: deviceId,
            email: secondaryemail,
            otp: userData?.otp,
            module: module,
            role: loginObject?.role || 3,
            companyKey: companyId
        };

        return await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.verifyOTP, payload, config) : () => axios.post(urls.verifyOTP, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setIsVerified(true)
                    window.showToast('success', res.msg);
                    updateUserData("otp", "");
                    return res;

                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    updateUserData('otp', '');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                updateUserData('otp', '');
            },
            isLoggedIn ? true : false
        );
    };

    const resetPassword = async () => {
        if (!deviceId) return;
        if (userData?.setPassword !== userData?.confirmPassword) {
            window.showToast('error', screenLabels?.passwords_do_not_match?.label_value || 'Password and confirm password do not match')
            return;
        }
        const payload = {
            deviceID: deviceId,
            password: userData?.setPassword,
            confirm_password: userData?.confirmPassword,
            email: emailStored?.toLowerCase() || userEmail || email,
            module: userModule,
            role: 3
        };

        await handleApiCall(
            isLoggedIn ? (config) => axios.post(urls.resetPassword, payload, config) : () => axios.post(urls.resetPassword, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', res.msg)
                    navigate('/profile');
                    updateUserData('setPassword', '');
                    updateUserData('confirmPassword', '');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");

            },
            isLoggedIn ? true : false
        );
    };
    const isLoggingOutRef = useRef(false);

    const doLogout = useCallback(async (redirect = true) => {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;

        let accessToken = storage.get('accessToken');

        try {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            const email = loginObject?.userEmail;

            if (!email || !deviceId || !userId) return;

            const payload = {
                deviceID: deviceId,
                email: email.toLowerCase() || userEmail,
                userID: userId,
                role: 3,
                token: accessToken
            };

            await handleApiCall(
                () => axios.post(urls.doLogout, payload),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        const selectedLanguage = storage.get('selectedLanguage');
                        const translatedLabels = storage.get('appLabels');
                        const isBiometricEnabled = storage.get("biometricAuthEnabled");
                        const deviceData = storage.get("device_info");

                        storage.clear();

                        storage.set('DeviceId', deviceId);
                        storage.set('device_info', deviceData);
                        storage.set('licenseType', licenseType);
                        storage.set('appLabels', translatedLabels);
                        storage.set('selectedLanguage', selectedLanguage);
                        if (isBiometricEnabled !== null && isBiometricEnabled !== undefined) {
                            storage.set('biometricAuthEnabled', isBiometricEnabled);
                        }
                        window?.ReactNativeWebView?.postMessage("googlesignOut");
                        getCompanyBranding();
                        if (redirect) {
                            // Use replace() (not href =) so the current protected
                            // page (e.g. /profile) is removed from browser history
                            // instead of leaving /login pushed on top of it.
                            // Otherwise pressing Back from /login returns to the
                            // now-unauthenticated /profile (restored from bfcache).
                            window.location.replace('/login');
                        }
                    } else {
                        window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");

                    }
                },
                (error) => {
                    window.showToast(
                        'error',
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"

                    );
                },
                true
            );
        } finally {
            // Reset after a delay to allow navigation to complete
            setTimeout(() => {
                isLoggingOutRef.current = false;
            }, 3000);
        }
    }, [loginObject?.userEmail, deviceId, userEmail, userId, getCompanyBranding, navigate]);

    const refreshPromiseRef = useRef(null);
    const isRefreshingRef = useRef(false);

    const getRefreshToken = useCallback(async () => {
        if (!deviceId) throw new Error("Device ID missing");

        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        refreshPromiseRef.current = (async () => {
            try {
                const refresh_token = storage.get("refreshToken");
                if (!refresh_token) return;

                const payload = {
                    deviceID: deviceId,
                    refreshToken: refresh_token
                };

                const res = await axios.post(urls.getRefreshToken, payload, {
                    skipLogoutInterceptor: true
                });

                const data = res.data;

                if (!data.status) {
                    throw new Error(data.error || "Refresh failed");
                }

                const accessToken = data?.auth?.access_token;
                const refreshToken = data?.auth?.refresh_token;

                const decoded = jwtDecode(accessToken);
                storage.set("accessToken", accessToken);
                storage.set("refreshToken", refreshToken);
                storage.set("tokenExpiry", decoded.exp);

                // ✅ UPDATE AXIOS DEFAULT HEADER (IMPORTANT)
                // axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

                return accessToken;

            } catch (err) {
                throw err;
            } finally {
                refreshPromiseRef.current = null;
            }
        })();

        return refreshPromiseRef.current;

    }, [deviceId]);

    const startTokenRefreshTimer = useCallback(() => {
        const expiry = storage.get("tokenExpiry");
        if (!expiry) return;

        const now = Math.floor(Date.now() / 1000);
        const timeLeft = expiry - now;

        const refreshTime = Math.max((timeLeft - 30) * 1000, 0);

        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        refreshTimerRef.current = setTimeout(async () => {
            try {
                await getRefreshToken();
                startTokenRefreshTimer();
            } catch {
                doLogout();
            }
        }, refreshTime);

    }, [getRefreshToken, doLogout]);

    useEffect(() => {
        // Public campaign scan/result routes are guest-only — never refresh tokens.
        if (window.location.pathname.startsWith('/invite/')) return;
        if (storage.get('isLoggedIn')) {
            startTokenRefreshTimer();
        }
    }, [startTokenRefreshTimer]);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,

            async (error) => {

                if (error.config?.skipLogoutInterceptor) {
                    return Promise.reject(error);
                }

                const status = error.response?.status;

                if (status === 423) {
                    if (!isLoggingOutRef.current) {
                        isLoggingOutRef.current = true;
                        storage.clear();
                        window?.ReactNativeWebView?.postMessage("googlesignOut");
                        getDeviceId();
                        getCompanyBranding();
                        navigate('/login');
                        setTimeout(() => {
                            isLoggingOutRef.current = false;
                        }, 3000);
                    }
                    return Promise.reject(error);
                }

                if (status === 401 && !error.config._retry) {
                    error.config._retry = true;

                    try {
                        const token = await getRefreshToken();

                        // ✅ Attach new token
                        error.config.headers["Authorization"] = `Bearer ${token}`;

                        return axios(error.config);

                    } catch (err) {
                        if (!isLoggingOutRef.current) {
                            doLogout();
                        }
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, []);


    // axios.interceptors.response.use(
    //     (response) => response,
    //     (error) => {
    //         if (error.config?.skipLogoutInterceptor) return Promise.reject(error);

    //         if ((error.response && error.response.status === 423) || (error.response && error.response.status === 401)) {
    //             setTimeout(() => {
    //                 doLogout();
    //             }, 2000);
    //         }
    //         return Promise.reject(error);
    //     }
    // );



    const updateSignupData = (field, value) => {
        setSignupData(prev => {
            let newData;
            if (typeof field === 'object' && field !== null) {
                // If the first argument is an object, merge it all at once
                newData = { ...prev, ...field };
            } else {
                // Otherwise, treat as single field/value
                newData = { ...prev, [field]: value };
            }

            if (email) newData.email = email;
            storage.set('signupData', newData);
            return newData;
        });
    };

    useEffect(() => {
        const savedSignupData = storage.get('signupData');
        if (savedSignupData) {
            setSignupData(prev => ({
                ...prev,
                ...savedSignupData
            }));
        }
    }, []);

    const createAccountScreenOne = () => {
        const firstName = signupData.firstName?.trim();
        const lastName = signupData.lastName?.trim();
        const phoneNumber = signupData.phoneNumber?.trim();
        const country = signupData.country?.trim();
        const time_zone = signupData.time_zone?.trim();

        if (!firstName || !lastName || !country || !time_zone) {
            window.showToast('error', screenLabels?.please_enter_all_required_fields?.label_value || 'Please enter all required fields!');
            return;
        }

        // if (signupData.accessCode?.trim() && signupData.accessCode.trim().length > 7) {
        //     window.showToast('error', screenLabels?.access_code_cannot_exceed_7_characters?.label_value || 'Access code cannot exceed 7 characters');
        //     return;
        // }

        storage.set('signupStep', 1);
        navigate('/signup/physical-profile');
    };

    const verifyAccessCode = useCallback(async (accessCode) => {
        if (!deviceId) return;
        const userEmail = storage.get('verifiedEmail');
        const payload = {
            deviceID: deviceId,
            accessCode,
            email: userEmail,
        };

        try {
            const response = await axios.post(urls.accessCodeVerification, payload);
            const res = response.data;

            if (res.status === true) {
                return { status: true };
            } else {
                return { status: false };
            }
        } catch (error) {
            return { status: false };
        }
    }, [deviceId]);

    const createAccountScreenTwo = (providedData) => {
        const dataToValidate = providedData || signupData;
        const { age, weight, height, race, gender, smoking, waistCircumference, diabetes } = dataToValidate;

        if (!age || !weight || !height || !race || !gender || !smoking || !waistCircumference || !diabetes) {
            window.showToast('error', screenLabels?.please_enter_all_required_fields?.label_value || "Please enter all required fields!");
            return;
        }

        if (diabetes === "yes") {
            window.showToast('error', screenLabels?.please_select_your_diabetes_type?.label_value || "Please select your diabetes type!");
            return;
        }

        const weightVal = parseFloat(weight);
        const heightVal = parseFloat(height);

        if (isNaN(weightVal) || weightVal <= 0) {
            window.showToast('error', screenLabels?.weight_must_be_a_number_greater_than_0?.label_value || "Weight must be a number greater than 0.");
            return;
        }

        if (isNaN(heightVal) || heightVal <= 0) {
            window.showToast('error', screenLabels?.height_must_be_a_number_greater_than_0?.label_value || "Height must be a number greater than 0.");
            return;
        }

        const ageVal = parseInt(age);
        const isUnder18 = ageVal < 18;

        if (isUnder18) {
            window.showToast('error', screenLabels?.you_must_be_at_least_18_years_old_to_continue?.label_value || "You must be at least 18 years old to continue.");
            return;
        }

        storage.set('signupStep', 2);
        if (is4Life) {
            navigate('/signup/lifestyle');
        } else {
            navigate('/signup/wellness-interests');
        }
    };

    const createAccountScreenThree = () => {
        const { activity, vegetables, grains, sugaryFoods, redMeat, fish } = signupData;

        if (!activity || !vegetables || !grains || !sugaryFoods || !redMeat || !fish) {
            window.showToast('error', screenLabels?.please_enter_all_required_fields?.label_value || "Please enter all required fields!");
            return;
        }

        storage.set('signupStep', 3);
        navigate('/signup/wellness-priorities');
    };

    const createAccountScreenFour = () => {
        const { wellnessCategory } = signupData;

        if (!Array.isArray(wellnessCategory) || wellnessCategory.length === 0) {
            window.showToast('error', screenLabels?.please_select_your_wellness_category?.label_value || "Please select your wellness category!");
            return;
        }
        storage.set('signupStep', 4);
        if (is4Life) {
            completeSignup();
        } else {
            navigate('/reset-password');
            storage.set('UserModule', 'setPassword');
        }
    }

    // storage.set("refKey", "jypeclno");

    const completeSignup = async () => {
        if (!deviceId) return;
        const requiredFields = [
            'firstName', 'lastName', 'email', 'age', 'weight',
            'height', 'race', 'gender', 'waistCircumference',
            'diabetes', 'wellnessCategory', 'smoking', 'password', 'confirmPassword',
            'vegetables', 'grains', 'sugaryFoods', 'redMeat', 'fish', 'activity', 'time_zone', 'country',
            // 'phoneNumber', 'accessCode'
        ];

        const isFromQR = !!storage.get('refKey');
        const missingFields = requiredFields.filter(field => {
            // Bypass password for 4Life
            if (is4Life && (field === 'password' || field === 'confirmPassword')) return false;
            // Bypass dietary questions for non-4Life (they skip Step 3 screen)
            if (!is4Life && (field === 'vegetables' || field === 'grains' || field === 'sugaryFoods' || field === 'redMeat' || field === 'fish' || field === 'activity')) return false;
            // Bypass phone number ONLY for 4Life (hidden in UI for them)
            if (is4Life && field === 'phoneNumber') return false;
            // Bypass access code for 4Life OR QR scan users (hidden in UI)
            if ((is4Life || isFromQR) && field === 'accessCode') return false;

            const value = signupData[field];
            if (Array.isArray(value)) return value.length === 0;
            return !value;
        });

        if (missingFields.length > 0) {
            window.showToast('error', is4Life ? (screenLabels?.please_complete_all_required_fields?.label_value || 'Please complete all required fields') : (screenLabels?.please_complete_all_previous_steps_before_setting_password?.label_value || 'Please complete all previous steps before setting password'));
            return;
        }

        if (!is4Life) {
            if (signupData.password !== signupData.confirmPassword) {
                window.showToast('error', screenLabels?.passwords_do_not_match?.label_value || 'Password and confirm password do not match');
                return;
            }

            if (!signupData.password || !signupData.confirmPassword) {
                window.showToast('error', screenLabels?.please_enter_password_and_confirm_password?.label_value || 'Please enter password and confirm password');
                return;
            }
        }


        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            licence_type: licenseType,
            firstName: signupData.firstName?.trim(),
            lastName: signupData.lastName?.trim(),
            email: signupData.email?.trim().toLowerCase() || email?.trim().toLowerCase(),
            secondaryEmail: signupData?.secondaryEmail?.trim().toLowerCase() || verifiedsecEmail?.trim().toLowerCase(),
            mobileNumber: signupData.phoneNumber || '',
            accessCode: signupData.accessCode || '',
            age: signupData.age,
            weight: signupData.weight,
            weightUnit: signupData.weightUnit,
            height: signupData.height,
            heightUnit: signupData.heightUnit,
            race: signupData.race,
            gender: signupData.gender,

            waistCircumference: signupData.waistCircumference,
            waistUnit: signupData.waistUnit,
            diabetes: signupData.diabetes,
            smoking: signupData.smoking,
            hypertension: signupData.hypertension,
            wellnessCategory: signupData.wellnessCategory || '',

            fruits: signupData.fruits,
            vegetables: signupData.vegetables,
            grains: signupData.grains,
            sugaryFoods: signupData.sugaryFoods,
            redMeat: signupData.redMeat,
            fish: signupData.fish,
            activity: signupData.activity,

            password: signupData.password,
            confirm_password: signupData.confirmPassword,
            token: oauthSuccessTokenRef.current || '',
            time_zone: signupData.time_zone,
            country: signupData.country,
            role: loginObject?.role || 3,
            ref_code: storage.get('refKey') || ''
        };

        const signupUrl = storage.get('isBack') ? urls.userRejoin : urls.completeSignup;
        await handleApiCall(
            () => axios.post(signupUrl, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    startTokenRefreshTimer();
                    window.showToast('success', res.msg || screenLabels?.account_created_successfully?.label_value || 'Account created successfully!');
                    const decodeAccessToken = jwtDecode(res?.token?.access_token);

                    storage.set('accessToken', res?.token?.access_token);
                    storage.set('refreshToken', res?.token?.refresh_token);
                    storage.set('tokenExpiry', decodeAccessToken?.exp);
                    storage.set('loginObj', decodeAccessToken);
                    storage.set('isLoggedIn', true);
                    storage.remove('isBack');
                    window.location.href = is4Life ? '/dashboard2' : '/dashboard';

                    setSignupData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        secondaryEmail: '',
                        phoneNumber: '',
                        accessCode: '',
                        age: '',
                        weight: '',
                        weightUnit: 'lbs',
                        heightUnit: 'feet',
                        waistUnit: 'cm',
                        height: '',
                        race: '',
                        gender: '',
                        waistCircumference: '',
                        diabetes: '',
                        smoking: '',
                        hypertension: '',
                        wellnessCategory: [],
                        fruits: '',
                        vegetables: '',
                        grains: '',
                        sugaryFoods: '',
                        redMeat: '',
                        fish: '',
                        activity: '',
                        country: '',
                        time_zone: '',
                        password: '',
                        confirmPassword: ''
                    });
                    storage.set('UserModule', 'Login');
                    storage.remove('signupData');
                    storage.remove('signupStep');
                    storage.remove('refKey');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            false
        );
    };

    const translateData = useCallback(async (lang, options = {}) => {
        if (!lang || !deviceId) return;

        // remoteVersion: when provided, stamp it into localStorage `version`
        //   after labels are successfully synced so the next load can compare.
        // silentError: suppress the error toast (used by the version-driven
        //   refresh on app open — a failed refresh keeps the cached labels).
        const { remoteVersion = null, silentError = false } = options;

        const payload = {
            deviceID: deviceId,
            language_code: lang,
            companyKey: companyId,
            role: 3
        };

        // Resolve the cache slot now, not when the response lands — the user may
        // have crossed between the invite flow and the main site meanwhile.
        const labelsKey = getLabelsStorageKey();

        await handleApiCall(
            () => axios.post(urls.traslateLabels, payload),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    let labels = res?.translations?.translations_labels?.data;
                    storage.set(labelsKey, labels);
                    // A slower fetch for a language the current route no longer
                    // uses must not replace the labels on screen.
                    const currentLang = storage.get(getLanguageStorageKey()) || 'en';
                    if (currentLang === lang && getLabelsStorageKey() === labelsKey) {
                        setTranslatedLabels(labels);
                    }
                    if (remoteVersion !== null) {
                        storage.set("version", remoteVersion);
                    }
                } else if (!silentError) {
                    window.showToast('error', res.msg || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                if (!silentError) {
                    window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            false
        );
    }, [deviceId]);

    // Device-id fetch is handled by the init effect above; this duplicate
    // effect generated a second device id (the API mints a new one each call),
    // which then re-triggered the language/label fetches. Removed.

    useEffect(() => {
        if (emailStored) {
            setEmail(emailStored);
            setSignupData(prev => ({
                ...prev,
                email: emailStored
            }));
        }
    }, [emailStored]);

    useEffect(() => {
        const seen = storage.get("notificationsSeen");
        if (seen === "true") {
            setIsNotificationReceived(false);
        }
    }, []);

    useEffect(() => {
        if (!userId) return;
        // Public campaign scan routes don't need the heart-beat / last-seen ping.
        if (window.location.pathname.startsWith('/invite')) return;

        const HEARTBEAT_INTERVAL = 2 * 60 * 1000; // 2 minutes

        const update = async () => {
            if (!deviceId) return;

            try {
                const token = storage.get("accessToken");
                if (!token) return;
                const payload = {
                    deviceID: deviceId,
                    userKey: userId,
                };

                // Record the time of this ping so a page refresh within the
                // interval window skips the redundant immediate call.
                storage.set("lastHeartBeat", Date.now());

                const response = await axios.post(urls.userLastSeen, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const res = response.data;
                if (res.status !== true) {
                    window.showToast("error", res.msg || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                } if (res.status === true && res.newNotification) {
                    // only set true when backend reports NEW notifications
                    setIsNotificationReceived(true);
                    storage.set("notificationsSeen", "false");
                }
            } catch (error) {
                window.showToast(
                    "error",
                    error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                );
            }
        };

        // Only ping immediately if the last heartbeat was more than one interval
        // ago. On a quick refresh (last ping still recent) we skip the extra call
        // and let the interval handle the next one — so heart-beat no longer
        // fires on every refresh.
        const lastHeartBeat = Number(storage.get("lastHeartBeat")) || 0;
        if (Date.now() - lastHeartBeat >= HEARTBEAT_INTERVAL) {
            update();
        }
        const interval = setInterval(update, HEARTBEAT_INTERVAL);
        return () => clearInterval(interval);
    }, [userId, deviceId]);

    const handleNotificationClick = () => {
        setIsNotificationReceived(false);
        localStorage.setItem("notificationsSeen", "true");
    };

    // One-time hydration: set the language and seed labels from cache so the UI
    // renders immediately without waiting on the version check below.
    useEffect(() => {
        // Public campaign scan routes don't need label translations.
        const storedLang = storage.get(getLanguageStorageKey());
        const storedLabels = storage.get(getLabelsStorageKey());
        setSelected(storedLang || 'en');
        if (storedLabels) {
            setTranslatedLabels(storedLabels);
        }
    }, []);

    // Label-version check — runs on mount AND on every route change (no reload
    // needed). If the remote version differs from the cached one (or nothing is
    // cached yet), refetch labels and stamp the new version. A fresh cache is a
    // no-op, so navigating between pages costs only one lightweight version GET.
    useEffect(() => {
        // Seed a baseline version for users who don't have one yet (e.g.
        // accounts created before this feature shipped). This runs on every
        // route change, so the `version` key is present on all pages. Future
        // admin version bumps then differ from this baseline and trigger a
        // label refetch.
        const currentVersion = storage.get("version");
        if (currentVersion === null || currentVersion === undefined) {
            storage.set("version", 100);
        }

        if (window.location.pathname.startsWith('/campaign/')) return;

        const checkLabelVersion = async () => {
            // Skip if a check is already running (rapid navigation).
            if (labelVersionCheckRef.current) return;
            labelVersionCheckRef.current = true;

            try {
                // Read the remote label version stamp. Endpoint failures leave
                // remoteVersion null → we keep the cached labels.
                let remoteVersion = null;
                try {
                    const versionRes = await axios.get(urls.labelVersion, {
                        withCredentials: false,
                        skipLogoutInterceptor: true,
                    });
                    remoteVersion = versionRes?.data?.version ?? null;
                } catch {
                    // Version endpoint unreachable — fall back to cached labels.
                }

                const storedVersion = storage.get("version");
                const storedLabels = storage.get(getLabelsStorageKey());
                const isStale =
                    remoteVersion !== null &&
                    String(storedVersion) !== String(remoteVersion);

                // Cache is fresh (and present) → nothing to do.
                if (storedLabels && !isStale) return;

                if (isStale) {
                    console.log("New label version found:", { storedVersion, remoteVersion });
                }

                // Refetch labels; stamp the new version, stay silent on failure
                // (keeps cached labels, retries on the next navigation).
                const lang = storage.get(getLanguageStorageKey()) || 'en';
                await translateData(lang, { remoteVersion, silentError: true });
            } finally {
                labelVersionCheckRef.current = false;
            }
        };

        checkLabelVersion();
    }, [translateData, location.pathname]);

    useEffect(() => {
        const storageLabels = storage.get(getLabelsStorageKey())
        if (isManualChange && selected) {
            translateData(selected);
        }
        else {
            setTranslatedLabels(storageLabels)
        }
    }, [selected, isManualChange, translateData]);

    // `selected` is a single in-memory value shared by the whole SPA session, so
    // navigating (client-side, no reload) between the invite guest flow and the
    // main site does NOT automatically re-point it at the other flow's storage
    // key — it just keeps whatever language was last active. That let a
    // language picked on an invite link keep rendering on normal site pages
    // (and vice versa) after navigating away, even though the two keys
    // (`inviteLanguageCode` vs `selectedLanguage`) were correctly isolated in
    // storage. Re-sync `selected` — and force a fresh translateData fetch
    // rather than trusting the single shared `appLabels` cache slot, which
    // holds only the most recently fetched language — whenever a route change
    // crosses that boundary.
    useEffect(() => {
        const langForRoute = storage.get(getLanguageStorageKey()) || 'en';
        if (langForRoute !== selected) {
            setSelected(langForRoute);
            setIsManualChange(true);
        }
    }, [location.pathname]);

    // Warm the voice manifest once per language after login so screens that use
    // voice audio (campaign invite, scan cues) resolve instantly from cache.
    // prefetchVoiceManifest dedupes via its per-language cache, so this hits the
    // API only when the language actually changes — not on every render/mount.
    useEffect(() => {
        if (!userId || !selected) return;
        prefetchVoiceManifest(selected);
    }, [userId, selected]);

    const isEmpty = (value) => {
        return (
            value === null ||
            value === undefined ||
            value === "" ||
            (typeof value === "string" && value.trim() === "") ||
            value === 0
        );
    };

    const getUserDetails = useCallback(async () => {

        if (!deviceId || !userId) return;

        const payload = {
            userKey: userId,
            deviceID: deviceId,
            role: loginObject?.role || 3
        };

        const cacheKey = `cachedUserDetails_${userId}`;
        const cached = storage.get(cacheKey);
        if (cached?.details) {
            setUserDetails(cached.details);
            setServings(cached.servings);
            setNotificationsEnabled(cached.details?.is_fcm_on);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls?.userDetails, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            const details = res?.details;
            const userInfo = {
                userKey: details?.user_key,
                companyKey: details?.company_key,
                firstName: details?.first_name,
                lastName: details?.last_name,
                userEmail: details?.email,
                userSecondaryEmail: details?.secondary_email,
                mobileNumber: details?.mobile_number || null,
                profilePhoto: details?.profile_photo || null,
                role: loginObject?.role || 3,
                deviceID: deviceId || "",
                time_zone: details?.time_zone
            };
            storage.set('loginObj', userInfo);

            if (res?.status === true) {
                const requiredFields = [
                    "gender",
                    "dob",
                    "height",
                    "weight",
                    "waist_circumference",
                    "smoking",
                    "wellness_category",
                    "diabetes",
                    "race",
                ];

                const isProfileIncomplete = requiredFields.some(
                    field => isEmpty(details[field])
                );

                // if (isProfileIncomplete) {
                //     window.showToast("error", "Please complete all required profile fields before starting the scan.");
                //     navigate('/profile-update');
                // }

                setUserDetails(details);
                setServings(res?.servings);
                setNotificationsEnabled(details?.is_fcm_on);
                storage.set(cacheKey, { details, servings: res?.servings });
            } else if (!cached) {
                window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, userId]);

    const getTimeZone = useCallback(async (search = "") => {
        if (!deviceId) return;

        const url = `${urls?.getTimeZone}?search=${search}`;
        const cacheKey = `cachedTimeZone_${search || 'all'}`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setTimeZone(cached);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.get(url, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                setTimeZone(res?.list);
                storage.set(cacheKey, res?.list);
            } else if (!cached) {
                window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId]);


    const updateUserDetails = async (dataToUpdate) => {
        if (!deviceId || !userId) return;
        const { height, weight, waist_circumference } = dataToUpdate
        const heightVal = parseFloat(height);
        const weightVal = parseFloat(weight);
        const waistVal = parseFloat(waist_circumference);

        if (isNaN(heightVal) || heightVal <= 0) {
            window.showToast('error', screenLabels?.height_must_be_a_number_greater_than_0?.label_value || 'Height must be a number greater than 0.');
            return;
        }

        if (isNaN(weightVal) || weightVal <= 0) {
            window.showToast('error', screenLabels?.weight_must_be_a_number_greater_than_0?.label_value || 'Weight must be a number greater than 0.');
            return;
        }

        if (isNaN(waistVal) || waistVal <= 0) {
            window.showToast('error', screenLabels?.waist_circumference_must_be_a_number_greater_than_0?.label_value || 'Waist circumference must be a number greater than 0.');
            return;
        }

        // if (
        //     mobileNumber &&
        //     !isValidPhoneNumber("+" + mobileNumber)
        // ) {
        //     window.showToast("error", "Please enter a valid phone number");
        //     return;
        // }

        // const dobDate = new Date(dob);
        const today = new Date();
        // const age = today.getFullYear() - dobDate.getFullYear();
        // const monthDiff = today.getMonth() - dobDate.getMonth();
        // const dayDiff = today.getDate() - dobDate.getDate();
        // const isUnder18 = age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)));

        // if (isUnder18) {
        //     window.showToast('error', 'User must be at least 18 years old.');
        //     return;
        // }

        const payload = {
            userID: userId,
            deviceID: deviceId,
            role: loginObject?.role || 3,
            ...dataToUpdate
        };

        return await handleApiCall(
            (config) => axios.post(urls?.updateUserDetails, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast('success', res.msg || screenLabels?.user_details_updated?.label_value || 'User details updated');
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
                return res;
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    };

    const deleteUserAcc = async () => {

        if (!deviceId || (is4Life ? !verifiedsecEmail : !email)) return;

        const payload = {
            // email: email,
            email: is4Life ? verifiedsecEmail : email,
            deviceID: deviceId,
            companyKey: companyId,
        };

        return await handleApiCall(
            (config) => axios.post(urls?.deleteUserAcc, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast('success', res.msg || screenLabels?.user_deleted?.label_value || 'User Deleted');
                    if (storage.get('isLoggedIn') == true) {
                        storage.clear();
                        window?.ReactNativeWebView?.postMessage("googlesignOut");
                        getDeviceId();
                        getCompanyBranding();
                        // replace so the deleted account's protected page isn't
                        // reachable via the browser Back button.
                        window.location.replace('/login');
                        // navigate('/login');
                    } else {
                        setIsThankYou(true);
                    }

                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
                return res;
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }


    const getFaqList = useCallback(async () => {
        if (!companyId || !deviceId || !userId) return;
        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            faq_id: '',
            adminKey: '',
            role: 3,
            userKey: userId,
            languageCode: selected,
        };

        await handleApiCall(
            (config) => axios.post(urls.getFaqList, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    setFaqList(res?.faq)
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [companyId, deviceId, userId, selected]);

    const uploadCropImg = async (blob) => {
        if (!userId) return;
        const formData = new FormData();
        formData.append("userID", userId);
        formData.append("image", blob, "profile.jpg");
        formData.append("slug", "user_profile");
        formData.append("role", 3);

        await handleApiCall(
            (config) =>
                axios.post(urls.uploadCroppedImg, formData, {
                    ...config,
                    headers: { "Content-Type": "multipart/form-data" },
                }),
            async (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast("success", screenLabels?.profile_image_updated?.label_value || "Profile image updated!");
                    await getUserDetails();
                } else {
                    window.showToast("error", res.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                console.error(error);
                window.showToast(
                    "error",
                    error?.response?.data?.error ||
                    error.message ||
                    screenLabels?.something_went_wrong?.label_value ||
                    "Something went wrong"
                );
            },
            true
        );
    };



    // Tracks whether the language list was already revalidated this app session,
    // so it's fetched once per load/refresh instead of once per screen.
    const languageListFetchedRef = useRef(false);

    const getLanguageList = useCallback(async () => {
        if (!deviceId) return;

        const cacheKey = `cachedLanguageList`;
        const cached = storage.get(cacheKey);

        if (cached) {
            setLanguages(cached);
        }

        if (languageListFetchedRef.current) return;
        languageListFetchedRef.current = true;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
        };

        try {
            const response = await axios.post(urls?.getLanguage, payload);
            const res = response?.data;
            if (res?.status === true) {
                setLanguages(res?.list);
                storage.set(cacheKey, res?.list);
            } else if (!cached) {
                window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            }
            return res;
        } catch (err) {
            // Allow a retry later if this session's fetch failed.
            languageListFetchedRef.current = false;
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId])



    const whiteLabel = useWhiteLabel();

    useEffect(() => {
        if (whiteLabel) {
            const root = document.documentElement;
            root.style.setProperty('--primaryColor', whiteLabel.web_primary_color || '');
            root.style.setProperty('--secondaryColor', whiteLabel.web_secondary_color || '');
            root.style.setProperty('--textColor', whiteLabel.web_text_color || '');
            root.style.setProperty('--headingColor', whiteLabel.web_heading_color || '');
            root.style.setProperty('--buttonColor', whiteLabel.web_button_color || '');
            root.style.setProperty('--buttonHoverColor', whiteLabel.web_button_color_hover || '');
            root.style.setProperty('--buttonTxtColor', whiteLabel.web_button_text_color || '');
            root.style.setProperty('--buttonTxtHoverColor', whiteLabel.web_button_text_color_hover || '');
            root.style.setProperty('--darkcolor', whiteLabel.web_text_color || '');

            root.style.setProperty('--headerBgColor', whiteLabel.web_header_background_color || '');
            root.style.setProperty('--headerTextColor', whiteLabel.web_header_text_color || '');
            root.style.setProperty('--footerBgColor', whiteLabel.web_footer_background_color || '');
            root.style.setProperty('--footerTextColor', whiteLabel.web_footer_text_color || '');
            root.style.setProperty('--footerIconColor', whiteLabel.web_footer_icon_color || '');
            root.style.setProperty('--footerMenuActiveColor', whiteLabel.web_footer_menu_active_color || '');
            root.style.setProperty('--sidebarBgColor', whiteLabel.web_sidebar_color || '');
            root.style.setProperty('--sidebarTextColor', whiteLabel.web_sidebar_text_color || '');
            root.style.setProperty('--sidebarIconColor', whiteLabel.web_sidebar_icon_color || '');
            root.style.setProperty('--sidebarActiveMenuColor', whiteLabel.web_sidebar_menu_active_color || '');
            root.style.setProperty('--pageBgColor', whiteLabel.web_page_background_color || '');
            root.style.setProperty('--linkColor', whiteLabel.web_link_color || '');

            const baseUrl = imagePath;
            const companyLogoUrl = whiteLabel?.company_logo ? baseUrl + whiteLabel.company_logo : '';
            const appHeaderLogoUrl = whiteLabel?.app_header_logo ? baseUrl + whiteLabel.app_header_logo : '';
            setLogoUrl(companyLogoUrl);
            setHeaderLogoUrl(appHeaderLogoUrl || companyLogoUrl);
        }
    }, [whiteLabel]);

    const uploadDocument = async () => {
        if (!deviceId || !userId || !companyId) return;
        const { file, selectedCategory, documentID } = documentData;

        if (!file || !selectedCategory || selectedCategory === 'all') {
            window.showToast("error", screenLabels?.both_category_and_file_are_required?.label_value || "Both category and file are required.");
            return;
        }

        const formData = new FormData();
        formData.append("companyID", companyId);
        formData.append("deviceID", deviceId);
        formData.append("userId", userId);
        formData.append("category", selectedCategory);
        formData.append("document", file);

        if (documentID) {
            formData.append("documentID", documentID);
        }

        await handleApiCall(
            (config) => axios.post(urls.uploadDocument, formData, {
                ...config,
                headers: {
                    ...config.headers,
                    'Content-Type': 'multipart/form-data',
                }
            }),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast("success", res.msg || (documentID ? (screenLabels?.updated_successfully?.label_value || "Updated successfully") : (screenLabels?.uploaded_successfully?.label_value || "Uploaded successfully")));
                    setDocumentData({
                        file: null,
                        previewUrl: "",
                        selectedCategory: "",
                        documentID: null,
                    });
                    storage.remove("documentUpdate")
                    navigate('/documents');
                } else {
                    window.showToast("error", res?.error || (documentID ? (screenLabels?.update_failed?.label_value || "Update failed") : (screenLabels?.upload_failed?.label_value || "Upload failed")));
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    };

    const getDocumentList = useCallback(async () => {
        if (!deviceId || !userId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            role: 3,
            adminKey: '',
            category: '',
            companyKey: companyId
        };

        await handleApiCall(
            (config) => axios.post(urls.getDocumentList, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    const formatted = res?.documents?.map((item) => {
                        const [date, time] = item.created_at.split(" ");
                        const [y, m, d] = date.split("-");
                        const isPM = +time.split(":")[0] >= 12;
                        const hour = (+time.split(":")[0] % 12) || 12;
                        const formattedTime = `${hour}:${time.split(":")[1]} ${isPM ? "PM" : "AM"}`;

                        return {
                            id: item.id,
                            key: item.category,
                            fileName: item.file_name,
                            date: `${d}/${m}/${y}`,
                            time: formattedTime,
                            fullPath: item.file_name,
                            userName: item.user_name,
                            status: item.status,
                            file_size: item.file_size
                        };
                    });

                    setUploadedFiles(formatted);
                } else {
                    window.showToast("error", res?.error || screenLabels?.upload_failed?.label_value || "Upload failed");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId, companyId]);

    const deletedocument = useCallback(async (documentId) => {

        if (!deviceId || !documentId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            documentID: documentId,
            role: 3
        };

        await handleApiCall(
            (config) => axios.post(urls.deleteDocument, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', res.msg);
                    setUploadedFiles(prevFiles => prevFiles.filter(doc => doc.id !== documentId));
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                }
            },
            (error) => {
                window.showToast(
                    'error',
                    error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            },
            true
        );
    }, [deviceId, userId]);

    const extractPolicyHtml = (res, key) => {
        if (!res || typeof res !== 'object') return '';
        return (
            res?.details?.[key] ||
            res?.data?.[key] ||
            res?.[key] ||
            ''
        );
    };

    const getTermsConditions = useCallback(async () => {
        if (!deviceId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            role: 3,
            companyKey: companyId,
            adminKey: '',
            languageCode: selected,
        };

        const cacheKey = `cachedTermsConditions`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setDocumentContent(prev => ({ ...prev, terms: cached }));
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.getTermsConditions, payload, {
                headers: { Authorization: `Bearer ${freshToken}` },
            });
            const res = response?.data;
            const terms = extractPolicyHtml(res, 'terms_conditions');
            if (terms) {
                setDocumentContent(prev => ({ ...prev, terms }));
                storage.set(cacheKey, terms);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, companyId, selected]);


    const getPrivacypolicy = useCallback(async () => {
        if (!deviceId || !companyId) return;

        // This endpoint expects the request as multipart/form-data, so send the
        // payload as FormData (axios sets the boundary'd Content-Type for us).
        const payload = new FormData();
        payload.append('deviceID', deviceId);
        payload.append('role', 3);
        payload.append('companyKey', companyId);
        payload.append('adminKey', '');
        payload.append('languageCode', selected);

        const cacheKey = `cachedPrivacyPolicy`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setDocumentContent(prev => ({ ...prev, privacy: cached }));
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.getPrivacypolicy, payload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${freshToken}`,
                },
            });
            const res = response?.data;
            const privacy = extractPolicyHtml(res, 'privacy_policy');
            if (privacy) {
                setDocumentContent(prev => ({ ...prev, privacy }));
                storage.set(cacheKey, privacy);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, companyId, selected]);

    const getArticlesList = useCallback(async (page = 1, perPage = 10) => {
        if (!deviceId || !companyId || !userId || !selected) return;

        if (prevLangRef.current !== selected || prevSearchRef.current !== search) {
            page = 1;
            prevLangRef.current = selected;
            prevSearchRef.current = search;
        }

        const payload = {
            deviceID: deviceId,
            role: 3,
            companyKey: companyId,
            userKey: userId,
            languageCode: selected,
            page: page,
            search: search
        };

        try {
            const freshToken = storage.get("accessToken");
            const config = {
                headers: { Authorization: `Bearer ${freshToken}` }
            };

            const response = await axios.post(urls.getArticlesList, payload, config);
            const res = response.data;

            if (res.status === true) {
                const articleData = res?.article;
                setArticlesList(articleData?.data || []);
                setPagination({
                    totalRecords: articleData?.total_records || 0,
                    totalPages: articleData?.total_pages || 1,
                    currentPage: page,
                    perPage: articleData?.per_page || perPage
                });
            } else {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (error) {
            window.showToast(
                "error",
                error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
            );
        }
    }, [deviceId, companyId, userId, selected, search]);


    const resetArticlesSearch = useCallback(() => {
        setSearch("");
        prevSearchRef.current = "";
        prevLangRef.current = selected;
    }, [selected]);

    const getArticleDetailsByID = useCallback(
        async (article_id) => {
            if (!deviceId || !companyId || !userId || !article_id) return;

            const payload = {
                deviceID: deviceId,
                role: 3,
                companyKey: companyId,
                userKey: userId,
                article_id: article_id,
            };

            setArticleDetails({});

            await handleApiCall(
                (config) => axios.post(urls.getArticldetailsById, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        setArticleDetails(res.article);
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );

    const normalizePercent = (val) => {
        if (val === null || val === undefined || isNaN(val)) return null;
        return Math.min(100, Math.max(0, Number(val)));
    };

    // Several factors come back without the `percent` triple the range bar needs
    // (immune/metabolic/sugar factors typically ship only min/max/val). Place the
    // value on a padded min..max scale so the biomarker still renders instead of
    // being dropped — which would otherwise hide the whole category.
    const derivePercents = (rawVal, rawMin, rawMax) => {
        if (rawVal === "" || rawVal === null || rawVal === undefined) return null;

        const value = Number(rawVal);
        const min = Number(rawMin);
        const max = Number(rawMax);
        if (isNaN(value) || isNaN(min) || isNaN(max) || max <= min) return null;

        const pad = (max - min) * 0.2;
        const scaleMin = min - pad;
        const scaleMax = max + pad;
        const toPercent = (n) =>
            Math.min(100, Math.max(0, ((n - scaleMin) / (scaleMax - scaleMin)) * 100));

        return {
            percent: toPercent(value),
            minPercent: toPercent(min),
            maxPercent: toPercent(max),
        };
    };

    const mapBiomarkers = (factors = {}, screenLabels, parentColor, normalizedScanDetails) => {
        return Object.entries(factors)
            .map(([key, val]) => {
                let percent = normalizePercent(val?.percent);
                let minPercent = normalizePercent(val?.min_percent);
                let maxPercent = normalizePercent(val?.max_percent);
                const avgValue = val?.avg_value ?? null;
                const normalValue = val?.normal_value ?? null;
                const unit = val?.unit || "";
                const value = val?.val ?? null;
                // scan_spectrum_v2 is no longer the only source of range data —
                // biomarker_score factors now carry their own `slider` (min/max/
                // position/gradient), which the dashboard bar renders directly.
                const slider = val?.slider || null;
                // Per-biomarker zone color the dashboard sorts by (green -> orange -> red).
                const color = val?.color || null;
                // zone.color mirrors what scan_spectrum_v2 used to provide —
                // the marker's exact color for the current position.
                const zone = val?.zone || null;

                if (percent === null) {
                    // Derive the whole triple together so the bar stays self-consistent.
                    // The top-level min/max are frequently "0"/"0" once a slider is
                    // present, so fall back to the slider's own min/max/input_value.
                    const derived =
                        derivePercents(val?.val, val?.min, val?.max) ||
                        (slider &&
                            derivePercents(slider.input_value, slider.min, slider.max));
                    if (!derived) return null;
                    percent = derived.percent;
                    minPercent = derived.minPercent;
                    maxPercent = derived.maxPercent;
                }
                if (minPercent === null || maxPercent === null) return null;

                // The raw factor `value` above is only a numeric code used for
                // the bar-position math — for display, prefer the real
                // translated reading already parsed onto this same key in
                // normalizedScanDetails (see findNormalizedValue), falling
                // back to the raw code when no such entry exists.
                const normalizedMatch = findNormalizedValue(key, normalizedScanDetails);
                const displayValue = normalizedMatch ? normalizedMatch.value : value;
                const displayUnit = normalizedMatch ? normalizedMatch.unit : unit;

                return {
                    name: formatBiomarkerName(key, screenLabels, normalizedScanDetails),
                    rawKey: key,
                    percent,
                    minPercent,
                    maxPercent,
                    normalValue,
                    avgValue,
                    unit: displayUnit,
                    value: displayValue,
                    shadowColor: parentColor,
                    slider,
                    color,
                    zone,
                };
            })
            .filter(Boolean);
    };

    const applyScanResponse = useCallback((res) => {
        setIsProfileUpdated(res?.is_profile === true);
        if (res?.is_active == false) {
            storage.set('isPlanActive', false)
        } else {
            storage.set('isPlanActive', true)
        }

        const vendorUserKey = res?.venderDetails?.vender_user_key;
        const vendorProjectKey = res?.venderDetails?.vender_project_key;
        storage.set('chatVendorUserKey', vendorUserKey);
        storage.set('chatVendorProjectKey', vendorProjectKey);
        const scanData = res?.scanList?.scan_data;
        const recommendations = res?.scanList?.recommendation || [];
        const biomarkerScore = res?.scanList?.biomarker_score;
        setTopSection(biomarkerScore?.top_section || null);

        // Reuse normalizeScanDetails' is4Life-aware labels for biomarker card
        // names instead of formatBiomarkerName() re-deriving them from the key.
        const normalizedForLabels = normalizeScanDetails(scanData || {}, screenLabels, is4Life);

        const dynamicTypes = {
            cardiovascular: {
                label: screenLabels?.cardiovascular?.label_value || "Cardiovascular",
                color: biomarkerScore?.cv_color,
                score: Number(biomarkerScore?.cv_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.cv_factors, screenLabels, biomarkerScore?.cv_color, normalizedForLabels),
            },
            immune: {
                label: screenLabels?.immune?.label_value || "Immune",
                color: biomarkerScore?.immune_color,
                score: Number(biomarkerScore?.immune_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.immune_factors, screenLabels, biomarkerScore?.immune_color, normalizedForLabels),
            },
            metabolic: {
                label: screenLabels?.metabolic?.label_value || "Metabolic",
                color: biomarkerScore?.metabolic_color,
                score: Number(biomarkerScore?.metabolic_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.metabolic_factors, screenLabels, biomarkerScore?.metabolic_color, normalizedForLabels),
            },
            stress: {
                label: screenLabels?.stress?.label_value || "Stress",
                color: biomarkerScore?.stress_color,
                score: Number(biomarkerScore?.stress_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.stress_factors, screenLabels, biomarkerScore?.stress_color, normalizedForLabels),
            },
            sugar: {
                label: screenLabels?.sugar?.label_value || "Sugar",
                color: biomarkerScore?.sugar_color,
                score: Number(biomarkerScore?.sugar_score || 0),
                biomarkers: mapBiomarkers(biomarkerScore?.sugar_factors, screenLabels, biomarkerScore?.sugar_color, normalizedForLabels),
            },
        };
        const cleanedDynamicTypes = Object.fromEntries(
            Object.entries(dynamicTypes).filter(
                ([_, type]) => type.biomarkers.length > 0
            )
        );

        setDynamicTypes(cleanedDynamicTypes);

        setScanDetails(scanData);
        // The spectrum maps sit on the scan wrapper for /scan-details, but the
        // campaign endpoint returns them at the response root alongside
        // `scanDetail` — accept either so the invite result renders the v2
        // colour bands instead of falling back to the legacy gradient.
        const spectrum =
            res?.scanList?.scan_spectrum || res?.scan_spectrum || scanData?.scan_spectrum || {};
        const spectrumV2 =
            res?.scanList?.scan_spectrum_v2 || res?.scan_spectrum_v2 || scanData?.scan_spectrum_v2 || {};
        setScanSpectrum(spectrum);
        setScanSpectrumV2(spectrumV2);
        if (Object.keys(spectrumV2).length === 0) {
            console.warn('[scan] no scan_spectrum_v2 in response — falling back to legacy spectrum', res);
        }
        setTempScanDetails(scanData);
        setRedirection({
            productLink: res?.scanList?.product_link,
            productShare: res?.scanList?.product_share
        });
        const formattedProducts = recommendations.map((item, index) => ({
            id: item.item_id || index,
            name: item.short_desc,
            image: item?.image || images.placeHolderImg,
            description: item.reasons?.length
                ? `Helps with ${item.reasons.slice(0, 3).map(r => r.name).join(" & ")}`
                : "Recommended for you",
        }));

        setProducts({
            scanId: res.scanList?.scanID,
            top4: formattedProducts.slice(0, 4),
            remaining: formattedProducts.slice(4),
            shareLink: "https://example.com/recommendations/123",
            date: res?.scanList?.converted_time,
        });
    }, [screenLabels]);

    const getScanDetails = useCallback(
        async (scanID = "") => {
            if (!deviceId || !userId || !companyId) return;

            const cacheKey = `cachedScanResponse_${userId}_${scanID || 'latest'}`;
            const cached = storage.get(cacheKey);

            if (cached) {
                applyScanResponse(cached);
            }

            const payload = {
                deviceID: deviceId,
                role: 3,
                userKey: userId,
                scanID: scanID,
                adminKey: "",
                licence_type: licenseType,
                companyKey: companyId
            };

            try {
                const freshToken = storage.get('accessToken');
                const response = await axios.post(urls.getScanDetails, payload, {
                    headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
                });
                const res = response?.data;
                if (res?.status === true) {
                    applyScanResponse(res);
                    storage.set(cacheKey, res);
                } else if (!cached) {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            } catch (err) {
                if (!cached) {
                    window.showToast(
                        "error",
                        err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                }
            }
        },
        [deviceId, companyId, userId, applyScanResponse]
    );
    const getScanHistory = useCallback(
        async (date) => {
            if (!deviceId || !companyId || !userId) return;

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                date: date,
            };

            await handleApiCall(
                (config) => axios.post(urls.getScanHistory, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        setScanHistory(res);
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );
    const getWellnessCat = useCallback(async () => {
        if (!deviceId) return;

        const payload = {
            deviceID: deviceId,
            langCode: storage.get(getLanguageStorageKey())
        };

        const cacheKey = `cachedWellnessCat`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setWellnessCat(cached);
        }

        try {
            const response = await axios.post(urls.listWellnessCat, payload);
            const res = response?.data;
            if (res?.status === true) {
                const data = res?.translations || res?.list;
                setWellnessCat(data);
                storage.set(cacheKey, data);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId]);

    const listBioMarker = useCallback(async () => {
        if (!deviceId || !selected) return;

        const payload = {
            deviceID: deviceId,
            languageCode: selected,
            companyKey: companyId,
            role: 3
        };

        const cacheKey = `cachedBioMarker_${selected}`;
        const cached = storage.get(cacheKey);
        if (cached) {
            setBioMarker(cached);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.listBioMarker, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                const data = res?.translations?.translations_labels?.data || res?.list;
                setBioMarker(data);
                storage.set(cacheKey, data);
            } else if (!cached) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (!cached) {
                window.showToast(
                    "error",
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                );
            }
        }
    }, [deviceId, selected]);

    // Disabled face scan SDK initialization as all scanning is now directed to mobile apps
    /*
    const initBiosenseSDK = useCallback(async () => {
        if (isBiosenseInitialized || !bioLicenseKey) return;
        try {
            await healthMonitorManager.initialize({ licenseKey: bioLicenseKey });
            setIsBiosenseInitialized(true);
            console.log("✅ Biosense SDK Initialized");
        } catch (error) {
            console.error("❌ Biosense SDK Init Error:", error);
            window.showToast("error", screenLabels?.biosense_sdk_initialization_failed?.label_value || "Biosense SDK initialization failed");
        }
    }, [isBiosenseInitialized, bioLicenseKey]);

    useEffect(() => {
        if (bioLicenseKey && !isBiosenseInitialized && isLoggedIn) {
            initBiosenseSDK();
        }
    }, [bioLicenseKey, isBiosenseInitialized, initBiosenseSDK, isLoggedIn]);
    */

    const editUserDetails = useCallback(
        async (details) => {

            if (!deviceId || !userId || !companyId) return;

            // let age = "";
            // if (details.dob) {
            //     const today = new Date();
            //     const dob = new Date(details.dob);
            //     age = today.getFullYear() - dob.getFullYear();
            //     const m = today.getMonth() - dob.getMonth();
            //     if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            //         age--;
            //     }
            // }

            if (!details.age || details.age < 18) {
                window.showToast("error", screenLabels?.user_must_be_at_least_18_years_old?.label_value || "User must be at least 18 years old.");
                setMsgLoader(false);
                return;
            }
            let translatedLabels = storage.get('appLabels');

            const labels = {
                pulse_wait: translatedLabels?.common?.pulse_wait,
                no_detect_face: translatedLabels?.common?.no_detect_face,
                no_detect_face_note: translatedLabels?.common?.no_detect_face_note,
                face_detect: translatedLabels?.common?.face_detect,
                scan_comment: translatedLabels?.common?.scan_comment,
                stop: translatedLabels?.common?.stop,
                adjust_face: translatedLabels?.common?.adjust_face,
                scan_stopped_note: translatedLabels?.common?.scan_stopped_note,
                scan_stopped: translatedLabels?.common?.scan_stopped,
            };

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                role: loginObject?.role || 3,
                gender: details.gender,
                weightUnit: details.weightUnit,
                weight: details.weight,
                heightUnit: details.heightUnit,
                height: details.height,
                smoking: details.smoking,
                dob: details.dob,
                age: details.age,
                race: details.race,
                labels: labels
            };

            await handleApiCall(
                (config) => axios.post(urls.updateUserData, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        let isPlanActive = storage.get('isPlanActive');
                        // Check if plan is active before proceeding
                        // if (!isPlanActive) {
                        //     setMsgLoader(false);
                        //     navigate('/plan-expired');
                        //     return;
                        // }
                        storage.set('loginObj', res?.data);
                        storage.set("startScanData", JSON.stringify(payload));
                        // Don't navigate here - let the caller handle navigation
                        setMsgLoader(false);
                        navigate("/web-face-scan", {
                            state: {
                                userProfile: {
                                    age: payload.age,
                                    gender: payload.gender,
                                    weight: payload.weight,
                                    weightUnit: payload.weightUnit,
                                    height: payload.height,
                                    heightUnit: payload.heightUnit,
                                    smoking: payload.smoking,
                                    race: payload.race,
                                    diabetes: payload.diabetes,
                                    hypertension: payload.hypertension,
                                    bloodGlucose: payload.bloodGlucose,
                                    cameraMode: "user",
                                },
                                backRoute: '/start-scan',
                            },
                        });
                    } else {
                        // Scan couldn't start — let the guideline screen show again
                        // next attempt.
                        resetGuidelines('scanGuidelinesSeen');
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                        setMsgLoader(false);
                        navigate('/plan-expired');
                    }
                },
                (error) => {
                    resetGuidelines('scanGuidelinesSeen');
                    window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    setMsgLoader(false);
                }
            );
        },
        [deviceId, userId, companyId, isPlanActive, navigate]
    );

    // Save scan results from web SDK
    const saveScanResults = useCallback(
        async (scanResult) => {
            if (!deviceId || !userId || !companyId) {
                console.error('Missing device, user, or company ID');
                return;
            }

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                role: loginObject?.role || 3,
                scanResult: scanResult,
                timestamp: new Date().toISOString(),
            };

            await handleApiCall(
                (config) => axios.post(urls.saveScanResults || urls.updateUserData, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        // Store results in tempScanDetails for results page
                        setTempScanDetails(scanResult);
                    } else {
                        console.error('Failed to save scan results:', res?.error);
                    }
                },
                (error) => {
                    console.error('Error saving scan results:', error);
                }
            );
        },
        [deviceId, userId, companyId]
    );

    // useEffect(() => {
    //     const disableContextMenu = (e) => e.preventDefault();
    //     const disableCopy = (e) => e.preventDefault();
    //     const disableSelection = (e) => e.preventDefault();

    //     document.addEventListener("contextmenu", disableContextMenu);
    //     document.addEventListener("copy", disableCopy);
    //     document.addEventListener("selectstart", disableSelection);

    //     return () => {
    //         document.removeEventListener("contextmenu", disableContextMenu);
    //         document.removeEventListener("copy", disableCopy);
    //         document.removeEventListener("selectstart", disableSelection);
    //     };
    // }, []);


    const exportScanDetails = useCallback(
        async (email, scanID, documentID, module, pdfLanguage) => {
            if (!deviceId || !userId || !email || (module !== "Document" && !scanID)) return;
            const storedLang = storage.get("selectedLanguage");
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                email,
                scanID,
                documentID,
                module,
                languageCode: storedLang,
                pdfLanguage: pdfLanguage || storedLang,
            };

            try {
                const response = await handleApiCall(
                    (config) => axios.post(urls.exportUserScanDetails, payload, config),
                    (response) => response.data
                );

                const res = response?.data || response;
                if (res?.status === true) {
                    window.showToast("success", res.message);
                    navigate(is4Life ? "/dashboard2" : "/dashboard");
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return false;
                }
            } catch (err) {
                window.showToast("error", screenLabels?.export_failed?.label_value || "Export failed");
                return false;
            }
        },
        [deviceId, userId]
    );

    // Downloads the scan report PDF directly to the device (no email).
    const downloadUserScanDetails = useCallback(
        async (scanID, pdfLanguage) => {
            if (!deviceId || !userId || !scanID) return false;
            const storedLang = storage.get("selectedLanguage");
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                scanID,
                languageCode: storedLang,
                pdfLanguage: pdfLanguage || storedLang,
            };

            try {
                const freshToken = storage.get("accessToken");
                const response = await axios.post(
                    urls.downloadUserScanDetails,
                    payload,
                    {
                        headers: freshToken
                            ? { Authorization: `Bearer ${freshToken}` }
                            : {},
                    }
                );

                const res = response?.data;

                // API returns JSON: { status, message, pdf: "<base64>" }
                if (res?.status === true && res?.pdf) {
                    const base64 = res.pdf.replace(
                        /^data:application\/pdf;base64,/,
                        ""
                    );
                    const byteChars = atob(base64);
                    const byteNumbers = new Array(byteChars.length);
                    for (let i = 0; i < byteChars.length; i++) {
                        byteNumbers[i] = byteChars.charCodeAt(i);
                    }
                    const blob = new Blob([new Uint8Array(byteNumbers)], {
                        type: "application/pdf",
                    });

                    const objUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = objUrl;
                    link.download = "Scan-Report.pdf";
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(objUrl);

                    window.showToast(
                        "success",
                        res?.message || screenLabels?.scan_report_generated_successfully?.label_value || "Scan report generated successfully."
                    );
                    return true;
                }

                window.showToast(
                    "error",
                    res?.error || res?.message || screenLabels?.download_failed?.label_value || "Download failed"
                );
                return false;
            } catch (err) {
                window.showToast(
                    "error",
                    err?.response?.data?.error || screenLabels?.download_failed?.label_value || "Download failed"
                );
                return false;
            }
        },
        [deviceId, userId]
    );

    const getHistoryDates = useCallback(async () => {
        if (!deviceId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            role: 3
        };
        await handleApiCall(
            (config) => axios.post(urls.getHistoryDates, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setHistoryDates(res?.list || [])
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId]);


    const getNotificationList = useCallback(async () => {
        if (!deviceId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            role: 3
        };
        await handleApiCall(
            (config) => axios.post(urls.getNotificationList, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setNotification(res?.list || [])
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId]);

    // Returns the raw response ({status, list, pagination}) instead of pushing
    // into shared context state — the Scan Logs page owns its own list/page
    // state since nothing else in the app needs this data.
    const getScanLogList = useCallback(async ({ scanType = "", scanStatus = "", page = 1 } = {}) => {
        if (!deviceId || !userId) return null;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            scanType,
            scanStatus,
            page,
        };
        return await handleApiCall(
            (config) => axios.post(urls.getScanLogList, payload, config),
            (response) => response.data,
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                return null;
            },
            true
        );
    }, [deviceId, userId]);

    const readNotifications = useCallback(async () => {
        if (!deviceId || !userId) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            role: 3
        };
        await handleApiCall(
            (config) => axios.post(urls.readNotification, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {

                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId]);

    const updateUserLanguage = useCallback(async () => {
        if (!deviceId || !userId || !selected) return;
        // Public campaign scan routes don't need to persist the user's language.
        if (window.location.pathname.startsWith('/invite/')) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            languageCode: selected
        };

        await handleApiCall(
            (config) => axios.post(urls.updateUserLanguage, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, userId, selected]);

    const notificationOnOff = useCallback(
        async (status) => {
            if (!deviceId || !userId) return;
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                companyKey: companyId,
                role: 3,
                status
            };

            try {
                const response = await handleApiCall(
                    (config) => axios.post(urls.notificationOnOff, payload, config),
                    (response) => response.data
                );
                const res = response?.data || response;
                if (res?.status === true) {
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return false;
                }
            } catch (err) {
                window.showToast("error", screenLabels?.export_failed?.label_value || "Export failed");
                return false;
            }
        },
        [deviceId, userId]
    );

    useEffect(() => {
        window.loginWithGoogle = async (token) => {
            let deviceID = storage.get('DeviceId');
            const payload = { deviceID, token, companyKey: companyId, licence_type: licenseType, device_data: deviceInfo };

            try {
                const response = await handleApiCall(
                    () => axios.post(urls.googleLogin, payload),
                    (response) => response.data
                );
                const res = response?.data || response;

                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);

                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res.email);
                    }

                    if (next === "dashboard") {
                        window.location.href = is4Life ? '/dashboard2' : '/dashboard';
                    } else if (next === "signup") {
                        setEmail(res.email);
                        storage.set('verifiedEmail', res.email);
                        updateSignupData("email", email);
                        navigate('/signup/account-details');
                    } else {
                        window.location.href = '/login';
                    }

                } else {
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }

            } catch (error) {
                console.error("❌ Google login error:", error);
                window.showToast('error', error?.message || screenLabels?.unexpected_error_occurred?.label_value || "Unexpected error occurred");
            }
        };

    }, []);

    useEffect(() => {
        window.loginWithApple = async (token) => {
            let deviceID = storage.get('DeviceId');
            const payload = { deviceID, token, companyKey: companyId, licence_type: licenseType, device_data: deviceInfo };

            try {
                const response = await handleApiCall(
                    () => axios.post(urls.appleLogin, payload),
                    (response) => response.data
                );
                const res = response?.data || response;

                if (res?.status === true) {
                    if (res?.msg) {
                        window.showToast('success', res?.msg);
                    }
                    const auth = res?.auth;
                    const next = res?.next;

                    if (auth && typeof auth === "object" && typeof auth.access_token === "string" && auth.access_token.trim() !== "") {
                        const decodeAccessToken = jwtDecode(auth.access_token);

                        storage.set('accessToken', auth.access_token);
                        storage.set('refreshToken', auth.refresh_token);
                        storage.set('tokenExpiry', decodeAccessToken?.exp);
                        storage.set('loginObj', decodeAccessToken);
                        storage.set('isLoggedIn', true);
                        let userId = decodeAccessToken?.userKey;
                        window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                        startTokenRefreshTimer();
                    } else {
                        storage.remove('accessToken');
                        storage.remove('refreshToken');
                        storage.remove('tokenExpiry');
                        storage.remove('loginObj');
                        storage.set('isLoggedIn', false);
                        storage.set('verifiedEmail', res.email);
                    }

                    if (next === "dashboard") {
                        window.location.href = is4Life ? '/dashboard2' : '/dashboard';
                    } else if (next === "signup") {
                        setEmail(res.email);
                        storage.set('verifiedEmail', res.email);
                        updateSignupData("email", email);
                        navigate('/signup/account-details');
                    } else {
                        window.location.href = '/login';
                    }

                } else {
                    window.showToast('error', res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }

            } catch (error) {
                console.error("❌ Google login error:", error);
                window.showToast('error', error?.message || screenLabels?.unexpected_error_occurred?.label_value || "Unexpected error occurred");
            }
        };

    }, []);

    const createCampaign = useCallback(
        async (campaignData) => {
            if (!deviceId || !userId || !companyId || !campaignData) return;
            const payload = {
                ...campaignData,
                deviceID: deviceId,
                companyKey: companyId,
                userKey: userId,
            }

            await handleApiCall(
                (config) => axios.post(urls.createCampaign, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        window.showToast('success', res?.message);
                        navigate("/invites-list");
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                }
            );
        },
        [deviceId, userId, companyId]
    );

    const getCampaignList = useCallback(async (search = "") => {
        if (!deviceId || !userId || !companyId) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            search: search || "",
        };

        if (search === "") {
            setIsLoading(true)
        }
        try {
            const freshToken = storage.get("accessToken");
            const config = {
                headers: { Authorization: `Bearer ${freshToken}` }
            };
            const response = await axios.post(urls.listCampaign, payload, config);

            const res = response.data;

            if (res.status !== true) {
                window.showToast(
                    "error",
                    res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                );
                return null;
            }

            // Cache the invitation quota so the list/create screens can warn
            // (and block) when nothing is left. Read defensively across the
            // possible field names the API may use.
            const total =
                Number(res?.total_limit ?? res?.invite_limit ?? res?.limit ?? 0) || 0;
            const used =
                Number(res?.used ?? res?.used_invites ?? res?.used_count ?? 0) || 0;
            const remaining =
                Number(
                    res?.remaining ??
                    res?.remaining_invites ??
                    res?.available ??
                    Math.max(0, total - used)
                ) || 0;
            const guestScanCount = Number(res?.guest_scan_count ?? 0) || 0;
            const inviteScanCount = Number(res?.invite_scan_count ?? 0) || 0;
            setInviteStats({
                total,
                used,
                remaining,
                guestScanCount,
                inviteScanCount,
                loaded: true,
            });

            return res;
        } catch (error) {
            window.showToast(
                "error",
                error?.response?.data?.error ||
                error.message ||
                screenLabels?.something_went_wrong?.label_value ||
                "Something went wrong"
            );
            return null;
        } finally {
            setIsLoading(false)
        }
    }, [deviceId, companyId, userId])

    const getCampaignDetails = useCallback(async (campaignCode) => {
        if (!deviceId || !userId || !companyId || !campaignCode) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            campaignCode: campaignCode
        };

        try {
            const response = await handleApiCall(
                (config) => axios.post(urls.campaignDetails, payload, config),
                (response) => {
                    const res = response.data;

                    if (res.status !== true) {
                        window.showToast(
                            "error",
                            res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                        );
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error ||
                        error.message ||
                        screenLabels?.something_went_wrong?.label_value ||
                        "Something went wrong"
                    );
                }
            );
            return response?.data || null;
        } catch (error) {
            return null;
        }
    }, [deviceId, companyId, userId]);

    const getCampaignScanList = useCallback(async (campaignCode, date = "", page = "") => {
        if (!deviceId || !userId || !companyId || !campaignCode) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            campaignCode: campaignCode,
            page: page,
            date: date
        };

        try {
            const response = await handleApiCall(
                (config) => axios.post(urls.campaignScanList, payload, config),
                (response) => {
                    const res = response.data;

                    if (res.status !== true) {
                        window.showToast(
                            "error",
                            res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                        );
                    }

                    return res;
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error ||
                        error.message ||
                        screenLabels?.something_went_wrong?.label_value ||
                        "Something went wrong"

                    );
                }
            );
            return response || null;
        } catch (error) {
            return null;
        }
    }, [deviceId, companyId, userId]);

    const stopCampaign = useCallback(async (campaignCode) => {
        if (!deviceId || !userId || !companyId || !campaignCode) return null;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            campaignCode: campaignCode
        };

        try {
            const response = await handleApiCall(
                (config) => axios.post(urls.stopCampaign, payload, config),
                (response) => {
                    const res = response.data;

                    if (res.status !== true) {
                        window.showToast(
                            "error",
                            res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                        );
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error ||
                        error.message ||
                        screenLabels?.something_went_wrong?.label_value ||
                        "Something went wrong"

                    );
                }
            );
            return response?.data || null;
        } catch (error) {
            return null;
        }
    }, [deviceId, companyId, userId]);

    const checkCampaign = useCallback(async (campaignCode) => {
        const devId = deviceId || storage.get('DeviceId');
        if (!devId || !campaignCode) return null;

        const payload = {
            deviceID: devId,
            campaignCode: campaignCode,
            companyKey: companyId,
            languageCode: storage.get(INVITE_LANGUAGE_STORAGE_KEY) || 'en',
        };

        try {
            const response = await axios.post(urls.checkCampaign, payload);
            return response?.data || null;
        } catch (error) {
            return null;
        }
    }, [deviceId]);

    // Public campaign guest registration — called from the campaign scan form.
    const createCampaignUser = useCallback(async (details) => {
        const devId = deviceId || storage.get('DeviceId');
        if (!devId) return null;

        const payload = { ...details, deviceID: devId };

        setMsgLoader(true);
        try {
            const response = await axios.post(urls.campaignCreateUser, payload);
            const res = response?.data || null;
            if (res && res.status === false) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
            return res;
        } catch (error) {
            window.showToast(
                "error",
                error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
            );
            return null;
        } finally {
            setMsgLoader(false);
        }
    }, [deviceId]);

    // Public campaign scan submission — called after the guest completes the face scan.
    const submitCampaignScan = useCallback(async ({ campUserKey, campaignCode, scanData, scanType, campaignUserProfile }) => {
        const devId = deviceId || storage.get('DeviceId');
        if (!devId) return null;

        const payload = {
            deviceID: devId,
            campUserKey,
            campaignCode,
            scanData,
            scanType,
            companyKey: companyId,
            languageCode: storage.get(INVITE_LANGUAGE_STORAGE_KEY) || 'en',
        };

        // The invite flow has no logged-in user or storage-tracked guest —
        // campUserKey is the identity this scan attempt logs under.
        const logInviteScanAttempt = (scanStatus, msg) =>
            logScanAttempt(scanStatus, msg, {
                userKey: "",
                guestUserKey: "",
                inviteUserKey: campUserKey || "",
                scanType: "invite",
            });

        setMsgLoader(true);
        try {
            const response = await axios.post(urls.campaignUserScan, payload);
            const res = response?.data || null;
            if (res && res.status === false) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                logInviteScanAttempt("fail", res?.error || "Something went wrong");
            }
            // Populate the same context state the dashboard/results screens read,
            // so the campaign result renders identically (scanDetails, scanSpectrum,
            // topSection, dynamicTypes…). The campaign API nests it under `scanDetail`.
            if (res?.status === true && res?.scanDetail) {
                logInviteScanAttempt("success", "scan success");
                // Quick-scan campaigns come back with an empty scanDetail (no
                // scan_data/recommendation) but still carry recommendations
                // under `product` — fall back to those so the result screen
                // has something to show instead of looking empty.
                const scanDetailWithFallback = {
                    ...res.scanDetail,
                    recommendation:
                        res.scanDetail.recommendation?.length > 0
                            ? res.scanDetail.recommendation
                            : res.product?.recommendations || [],
                };
                const applied = { ...res, scanList: scanDetailWithFallback };
                applyScanResponse(applied);
                // Keep it for a repeat visit to the same invite link.
                storage.set(campaignResultKey(campaignCode), {
                    response: applied,
                    campaignUserProfile: campaignUserProfile || { campUserKey, scanType },
                });
            }
            return res;
        } catch (error) {
            window.showToast(
                "error",
                error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
            );
            logInviteScanAttempt(
                "fail",
                error?.response?.data?.error || error.message || "Something went wrong"
            );
            return null;
        } finally {
            setMsgLoader(false);
        }
    }, [deviceId, applyScanResponse, logScanAttempt]);

    // Replays a stored campaign scan into the same context state the result
    // screen reads, so `/invite/:code/result` renders exactly as it did right
    // after the scan. Returns the cached entry (the caller needs
    // campaignUserProfile for the route state) or null when nothing is stored.
    const restoreCampaignScanResult = useCallback((campaignCode) => {
        if (!campaignCode) return null;
        const cached = storage.get(campaignResultKey(campaignCode));
        if (!cached?.response) return null;

        // The cached `response` is `{...res, scanList: ...}` from submitCampaignScan,
        // but depending on how the backend shaped that particular API response,
        // the useful fields can sit directly on it or one level deeper under its
        // own `response` key — check both instead of assuming one exact shape.
        const payload =
            cached.response.scanList || cached.response.scanDetail
                ? cached.response
                : cached.response.response || cached.response;
        const scanList = payload?.scanList || payload?.scanDetail;

        // The result screen only bounces back to the form when there's
        // neither scan_data nor recommendations (see hasAnyResult in
        // CampaignResult.jsx) — quick-scan campaigns legitimately have no
        // scan_data but do have recommendations, so accept either here too,
        // or a cached quick-scan result would wrongly fall through to the
        // "already participated" error instead of replaying.
        const hasResult =
            Boolean(scanList?.scan_data) || (scanList?.recommendation?.length || 0) > 0;
        if (!hasResult) return null;

        const normalizedResponse = { ...payload, scanList };
        applyScanResponse(normalizedResponse);
        return { ...cached, response: normalizedResponse };
    }, [applyScanResponse]);

    // Public campaign scan export — emails the guest their report.
    const exportCampaignScan = useCallback(
        async ({ campUserKey, scanID, email, name = "", languageCode = "en", pdfLanguage }) => {
            const devId = deviceId || storage.get('DeviceId');
            if (!devId || !campUserKey || !scanID || !email) return false;

            const payload = {
                deviceID: devId,
                campUserKey,
                scanID,
                email,
                name,
                languageCode,
                pdfLanguage: pdfLanguage || languageCode,
            };

            setMsgLoader(true);
            try {
                const response = await axios.post(urls.campaignExportScan, payload);
                const res = response?.data || null;
                if (res?.status === true) {
                    window.showToast(
                        "success",
                        res?.msg || res?.message || screenLabels?.please_check_your_email_to_get_the_report?.label_value || "Please check your email to get the report."
                    );
                    return true;
                }
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                return false;
            } catch (error) {
                window.showToast(
                    "error",
                    error?.response?.data?.error || error.message || screenLabels?.export_failed?.label_value || "Export failed"
                );
                return false;
            } finally {
                setMsgLoader(false);
            }
        },
        [deviceId]
    );

    const saveGuestUser = useCallback(
        async (details) => {
            if (!deviceId || !userId || !companyId) return;

            // let age = "";
            // if (details.dob) {
            //     const today = new Date();
            //     const dob = new Date(details.dob);
            //     age = today.getFullYear() - dob.getFullYear();
            //     const m = today.getMonth() - dob.getMonth();
            //     if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            //         age--;
            //     }
            // }
            if (!details.age || details.age < 18) {
                window.showToast("error", screenLabels?.user_must_be_at_least_18_years_old?.label_value || "User must be at least 18 years old.");
                setMsgLoader(false);
                return;
            }
            let translatedLabels = storage.get('appLabels');

            const labels = {
                pulse_wait: translatedLabels?.common?.pulse_wait,
                no_detect_face: translatedLabels?.common?.no_detect_face,
                no_detect_face_note: translatedLabels?.common?.no_detect_face_note,
                face_detect: translatedLabels?.common?.face_detect,
                scan_comment: translatedLabels?.common?.scan_comment,
                stop: translatedLabels?.common?.stop,
                adjust_face: translatedLabels?.common?.adjust_face,
                scan_stopped_note: translatedLabels?.common?.scan_stopped_note,
                scan_stopped: translatedLabels?.common?.scan_stopped,
            };

            const payload = {
                firstName: details.firstName,
                lastName: details.lastName,
                email: details.email,
                deviceID: deviceId,
                parentUserKey: userId,
                guestUserKey: details.guestUserKey,
                companyKey: companyId,
                role: loginObject?.role || 3,
                gender: details.gender,
                weightUnit: details.weightUnit,
                weight: details.weight,
                heightUnit: details.heightUnit,
                waistUnit: details.waistUnit,
                waist_circumference: details.waist_circumference,
                height: details.height,
                smoking: details.smoking,
                dob: details.dob,
                diabetes: details.diabetes,
                hypertension: details.hypertension,
                race: details.race,
                age: details.age,
                labels: labels,
                servings: details.servings,
            };

            await handleApiCall(
                (config) => axios.post(urls.saveGuestUser, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        let isPlanActive = storage.get('isPlanActive');
                        // Check if plan is active before proceeding with guest scan
                        // if (!isPlanActive) {
                        //     setMsgLoader(false);
                        //     navigate('/plan-expired');
                        //     return;
                        // }
                        storage.set("guestUserKey", res?.userData?.guestUserID);
                        storage.set("scanGuest", true);
                        storage.set("startScanData", JSON.stringify(payload));
                        navigate("/web-face-scan", {
                            state: {
                                userProfile: {
                                    age: payload.age,
                                    gender: payload.gender,
                                    weight: payload.weight,
                                    weightUnit: payload.weightUnit,
                                    height: payload.height,
                                    heightUnit: payload.heightUnit,
                                    smoking: payload.smoking,
                                    race: payload.race,
                                    diabetes: payload.diabetes,
                                    hypertension: payload.hypertension,
                                    bloodGlucose: payload.bloodGlucose,
                                    cameraMode: "user",
                                },
                                backRoute: '/guest-scan',
                                // Lets the scan screen's stop/close return to a
                                // pre-filled guest form (see GuestScan draft).
                                backState: {
                                    restoreDraft: true,
                                    guestData: res?.userData?.guestUserID
                                        ? { guest_user_key: res.userData.guestUserID }
                                        : undefined,
                                },
                            },
                        });
                        // navigate('/mobile-scan-prompt');
                    } else {
                        // Scan couldn't start — let the guideline screen show again
                        // next attempt.
                        resetGuidelines('scanGuidelinesSeen');
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                        setMsgLoader(false);
                        navigate('/plan-expired');
                    }
                },
                (error) => {
                    resetGuidelines('scanGuidelinesSeen');
                    window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    setMsgLoader(false);
                }
            );
        },
        [deviceId, userId, companyId, isPlanActive, navigate]
    );

    const getGuestUserList = useCallback(async (shouldNavigate = true) => {
        if (!deviceId || !userId || !companyId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: 3
        };

        await handleApiCall(
            (config) => axios.post(urls.getGuestUserList, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    setGuestList(res || []);
                    if (!shouldNavigate) return true;

                    if (res.list && res.list.length > 0) {
                        navigate('/guest-list');
                    } else {
                        navigate('/guest-scan');
                    }
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    if (shouldNavigate) {
                        navigate('/guest-scan');
                    }
                    return false;
                }
            },
            (error) => {
                window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                if (shouldNavigate) {
                    navigate('/guest-scan');
                }
                return false;
            }
        );
    }, [deviceId, companyId, userId, navigate]);

    const getGuestUserDetails = useCallback(async (guestUserKey) => {
        if (!deviceId || !userId || !companyId || !guestUserKey) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            guestUserKey: guestUserKey,
            role: 3
        };

        return await handleApiCall(
            (config) => axios.post(urls.getGuestUserDetails, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    return res.detail;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return null;
                }
            },
            (error) => {
                window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                return null;
            }
        );
    }, [deviceId, companyId, userId]);

    const deleteGuestUser = useCallback(async (guestUserKey) => {
        if (!deviceId || !userId || !guestUserKey) return;

        const payload = {
            deviceID: deviceId,
            userKey: userId,
            guestUserKey: guestUserKey,
            role: loginObject?.role || 3
        };

        const result = await handleApiCall(
            (config) => axios.post(urls.deleteGuestUser, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    window.showToast("success", res.msg || screenLabels?.guest_user_deleted_successfully?.label_value || "Guest user deleted successfully");
                    return true;
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    return false;
                }
            },
            (error) => {
                window.showToast("error", error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                return false;
            }
        );
        if (result) {
            await getGuestUserList(false);
        }
        return result;
    }, [deviceId, userId, getGuestUserList, loginObject?.role]);

    const getUserScanLimit = useCallback(async (scan_type) => {
        if (!deviceId || !companyId || !userId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: 3,
            ...(scan_type ? { scan_type } : {}),
        };

        const cacheKey = `cachedScanLimit_${userId}`;
        const cached = storage.get(cacheKey);
        if (cached !== null && cached !== undefined) {
            setScanLimit(cached);
        }

        try {
            const freshToken = storage.get('accessToken');
            const response = await axios.post(urls.getScanLimit, payload, {
                headers: freshToken ? { Authorization: `Bearer ${freshToken}` } : {},
            });
            const res = response?.data;
            if (res?.status === true) {
                setScanLimit(res?.remain_status);
                storage.set(cacheKey, res?.remain_status);
            } else if (cached === null || cached === undefined) {
                window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
            }
        } catch (err) {
            if (cached === null || cached === undefined) {
                window.showToast(
                    'error',
                    err?.response?.data?.error || err.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong'
                );
            }
        }
    }, [deviceId, companyId, userId]);

    const updateUserServings = useCallback(async (serving) => {
        if (!deviceId || !companyId || !userId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
            fruits: serving.fruits,
            vegetables: serving.vegetables,
            grains: serving.grains,
            sugaryfoods: serving.sugaryfoods,
            redmeat: serving.redmeat,
            fish: serving.fish,
            activity: serving.activity
        };

        await handleApiCall(
            (config) => axios.post(urls.updateServings, payload, config),
            (response) => {
                const res = response.data;
                if (res.status === true) {
                    window.showToast('success', res.message);
                } else {
                    window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, companyId, userId]);


    const fourLifeUserLogin = useCallback(async () => {
        if (!deviceId) return;

        const payload = {
            deviceID: deviceId,
            company_id: process.env.REACT_APP_COMPANY_ID,
            email: fourLifeuserData.userName,
            password: fourLifeuserData.password,
            licence_type: licenseType,
        };

        if (!fourLifeuserData.userName) {
            window.showToast("error", screenLabels?.please_enter_your_username?.label_value || "Please enter your username.");
            return;
        }

        if (!fourLifeuserData.password) {
            window.showToast("error", screenLabels?.please_enter_your_password?.label_value || "Please enter your password.");
            return;
        }

        await handleApiCall(
            () => axios.post(urls.userLogin, payload),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    if (res?.next_screen == "signup") {
                        navigate('/signup/account-details');
                        return;
                    }
                    storage.set('isLoggedIn', true)
                    const decodeAccessToken = jwtDecode(res?.token?.access_token);

                    storage.set('accessToken', res?.token?.access_token);
                    storage.set('refreshToken', res?.token?.refresh_token);
                    storage.set('tokenExpiry', decodeAccessToken?.exp);
                    startTokenRefreshTimer();
                    storage.set('loginObj', decodeAccessToken)
                    storage.remove('verifiedEmail');
                    window.location.href = is4Life ? '/dashboard2' : '/dashboard';
                    storage.set('UserModule', 'Login')
                    let userId = decodeAccessToken?.userKey;
                    window.ReactNativeWebView?.postMessage(`userID|${userId}`);
                    updateUserData('password', '')
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('password', '')
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                updateUserData('password', '')
            },
            false
        );
    }, [deviceId, companyId, fourLifeuserData, licenseType]);

    const addUserToChat = async () => {
        if (!deviceId || !companyId || !userId) return;
        const loginObj = storage.get("loginObj");
        let firstName = loginObj?.firstName || loginObj?.first_name || "User";
        let lastName = loginObj?.lastName || loginObj?.last_name || "";
        let email = loginObj?.userEmail || loginObj?.email || "";
        const payload = {
            deviceID: deviceId,
            userID: userId,
            firstName: firstName,
            lastName: lastName,
            // projectKey: process.env.REACT_APP_CHAT_AI_PROJECT_KEY || "",
            projectKey: '1hdsa47' || "",
            email: email
        };

        await handleApiCall(
            (config) => axios.post(urls.addUserToChat, payload, config),
            async (response) => {
                const res = response.data;
                if (res.status === true) {
                    const vendorUserKey = res?.vender_user_key;
                    storage.set('chatVendorUserKey', vendorUserKey);
                    storage.set('chatVendorProjectKey', payload.projectKey);

                    if (vendorUserKey) {
                        try {
                            const vendorPayload = {
                                deviceID: deviceId,
                                vendorUserID: vendorUserKey
                            };
                            const vendorRes = await axios.post(urls.venderUserLogin, vendorPayload, {
                                headers: { Authorization: `Bearer ${storage.get('accessToken')}` }
                            });
                            if (vendorRes.data?.status === true) {
                                storage.set('chatVendorAccessToken', vendorRes.data.token?.access);
                            } else {
                                console.error('Failed to log in vendor user:', vendorRes.data?.error);
                            }
                        } catch (err) {
                            console.error('Error logging in vendor user:', err);
                        }
                    }
                } else {
                    console.error('Failed to add user to chat:', res.error);
                }
            },
            (error) => {
                console.error('Error adding user to chat:', error);
            },
            true
        );
    };

    const getRecProductList = useCallback(
        async (scanID = "") => {
            if (!deviceId || !companyId || !userId) return;

            const payload = {
                deviceID: deviceId,
                userKey: userId,
                scanID: scanID,
                role: loginObject?.role || 3,
            };

            await handleApiCall(
                (config) => axios.post(urls.recProductList, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {
                        setFullProducts({
                            scanId: scanID,
                            fullList: res?.list || [],
                        });
                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );

    const fetchFreshProducts = useCallback(
        async (scanID = "") => {
            if (!deviceId || !companyId || !userId) return;
            await new Promise((resolve) => setTimeout(resolve, 4000));
            try {
                const token = storage.get('accessToken');
                const payload = {
                    deviceID: deviceId,
                    userKey: userId,
                    scanID: scanID,
                    role: loginObject?.role || 3,
                };
                const response = await axios.post(urls.recProductList, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const res = response.data;
                if (res.status === true) {
                    const list = res?.list || [];
                    const formatted = list.map((item, index) => ({
                        id: item.item_id || index,
                        name: item.short_desc,
                        image: item?.image !== null ? item?.image : images.placeHolderImg,
                        description: item.reasons?.length
                            ? `Helps with ${item.reasons.slice(0, 3).map(r => r.name).join(" & ")}`
                            : "Recommended for you",
                    }));
                    setProducts(prev => ({
                        ...prev,
                        scanId: scanID,
                        top4: formatted.slice(0, 4),
                        remaining: formatted.slice(4),
                    }));
                    setIsProfileUpdated(false);
                }
            } catch {
                setIsProfileUpdated(false);
            }
        },
        [deviceId, companyId, userId]
    );

    const productCheckout = useCallback(
        async (productId) => {
            const payload = {
                deviceID: deviceId,
                userKey: userId,
                productId: productId,
                role: loginObject?.role || 3,
            };

            await handleApiCall(
                (config) => axios.post(urls.productCheckout, payload, config),
                (response) => {
                    const res = response.data;
                    if (res.status === true) {

                    } else {
                        window.showToast("error", res?.error || screenLabels?.something_went_wrong?.label_value || "Something went wrong");
                    }
                },
                (error) => {
                    window.showToast(
                        "error",
                        error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || "Something went wrong"
                    );
                },
                true
            );
        },
        [deviceId, companyId, userId]
    );

    useEffect(() => {
        updateUserLanguage();
    }, [selected, updateUserLanguage]);

    const getFourLifeScanHistory = useCallback(async () => {
        if (!deviceId) return;

        const payload = {
            deviceID: deviceId,
            companyKey: companyId,
            userKey: userId,
            role: loginObject?.role || 3,
        };


        await handleApiCall(
            (config) => axios.post(urls.getFourLifeScanHistory, payload, config),
            (response) => {
                const res = response.data;
                if (res?.status === true) {
                    setFourLifeScanHistory(res?.list || [])
                } else {
                    window.showToast('error', res.error || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
                    updateUserData('password', '')
                }
            },
            (error) => {
                window.showToast('error', error?.response?.data?.error || error.message || screenLabels?.something_went_wrong?.label_value || 'Something went wrong');
            },
            true
        );
    }, [deviceId, companyId, fourLifeuserData]);

    // ── Blood-pressure calibration baseline APIs ──────────────────────────
    // Backend persists one baseline per user. `save` is called when the user
    // completes the 3-capture calibration flow; `get` hydrates the cached
    // baseline on app start; `delete` is called when the user re-calibrates.
    const saveBPCalibration = useCallback(async (baseline) => {
        if (!deviceId || !userId || !baseline) return null;
        const payload = {
            deviceID: deviceId,
            userKey: userId,
            companyKey: companyId,
            systolicDiff: Math.round(baseline.systolicDiff),
            diastolicDiff: Math.round(baseline.diastolicDiff),
            pulseDiff: Math.round(baseline.pulseDiff),
            calibratedAt: baseline.calibratedAt,
        };
        return await handleApiCall(
            (config) => axios.post(urls.saveBPCalibration, payload, config),
            (response) => {
                const res = response?.data;
                if (res?.status === true) {
                    return res?.data;
                }
                if (res?.error) window.showToast('error', res.error);
                return null;
            },
        );
    }, [deviceId, userId, companyId]);

    const getBPCalibration = useCallback(async () => {
        if (!deviceId || !userId) return null;
        return await handleApiCall(
            (config) => axios.post(
                urls.getBPCalibration,
                { deviceID: deviceId, userKey: userId, companyKey: companyId },
                config,
            ),
            (response) => {
                // Backend returns snake_case fields. Normalize to the camelCase
                // shape the rest of the app expects, so every consumer can read
                // `systolicDiff`, `calibratedAt`, etc. without caring about the
                // wire format.
                const raw = response?.data?.data;
                if (!raw) return null;
                const rawDate = raw.calibratedAt ?? raw.calibrated_at;
                if (!rawDate) return null;
                // Backend may return "YYYY-MM-DD HH:mm:ss". Convert to a
                // parseable ISO-ish form so Safari does not return Invalid Date.
                const calibratedAt = typeof rawDate === 'string'
                    ? rawDate.replace(' ', 'T')
                    : rawDate;
                return {
                    systolicDiff: Number(raw.systolicDiff ?? raw.systolic_diff ?? 0),
                    diastolicDiff: Number(raw.diastolicDiff ?? raw.diastolic_diff ?? 0),
                    pulseDiff: Number(raw.pulseDiff ?? raw.pulse_diff ?? 0),
                    calibratedAt,
                    pointCount: Number(raw.pointCount ?? raw.point_count ?? 0),
                };
            },
        );
    }, [deviceId, userId, companyId]);

    const deleteBPCalibration = useCallback(async () => {
        if (!deviceId || !userId) return null;
        return await handleApiCall(
            (config) => axios.post(
                urls.deleteBPCalibration,
                { deviceID: deviceId, userKey: userId, companyKey: companyId },
                config,
            ),
            (response) => response?.data?.status === true,
        );
    }, [deviceId, userId, companyId]);

    return (
        <GlobalContext.Provider
            value={{
                deviceId,
                isLoading,
                startScan,
                showPasswords,
                togglePassword,
                updateUserData,
                userData,
                checkUserRegistration,
                email,
                loginByPassword,
                handlenavigateToEmail,
                sendEmailOTP,
                verifyOTP,
                setEmail,
                doLogout,
                resetPassword,
                resendEmailOTP,
                timer,
                resendDisabled,
                updateSignupData,
                createAccountScreenOne,
                createAccountScreenTwo,
                createAccountScreenThree,
                createAccountScreenFour,
                translatedLabels,
                setSelected,
                selected,
                setIsManualChange,
                translateData,
                signupData,
                completeSignup,
                setSignupData,
                getUserDetails,
                userDetails,
                updateUserDetails,
                isVerified,
                setIsVerified,
                deleteUserAcc,
                handleChangePassword,
                getFaqList,
                faqList,
                uploadCropImg,
                selectedBillType,
                setSelectedBillype,
                getLanguageList,
                languages,
                logoUrl,
                headerLogoUrl,
                uploadDocument,
                documentData,
                setDocumentData,
                getDocumentList,
                uploadedFiles,
                getTermsConditions,
                documentContent,
                getPrivacypolicy,
                getScanDetails,
                scanDetails,
                topSection,
                scanSpectrum,
                scanSpectrumV2,
                getArticlesList,
                articlesList,
                pagination,
                getArticleDetailsByID,
                articleDetails,
                getScanHistory,
                scanHistory,
                deletedocument,
                getWellnessCat,
                wellnessCat,
                listBioMarker,
                bioMarker,
                editUserDetails,
                saveScanResults,
                tempScanDetails,
                setMsgLoader,
                showExportModal,
                setShowExportModal,
                userEmail,
                showCalendar,
                setShowCalendar,
                calendarRef,
                exportScanDetails,
                globalUserEmail,
                setGlobalUserEmail,
                getHistoryDates,
                historyDates,
                setSearch,
                search,
                resetArticlesSearch,
                wellnessLabel,
                wellnessLabelNormal,
                getNotificationList,
                getScanLogList,
                notification,
                readNotifications,
                hasNotification,
                setHasNotification,
                isNotificationReceived,
                setIsNotificationReceived,
                handleNotificationClick,
                getTimeZone,
                timeZone,
                notificationOnOff,
                setNotificationsEnabled,
                notificationsEnabled,
                handleDeletAccount,
                isPlanActive,
                setIsPlanActive,
                showOTPSection,
                setShowOTPSection,
                getuserDetails,
                setUserDatav2,
                userDatav2,
                verifyAccessCode,
                isThankYou,
                createCampaign,
                getCampaignList,
                inviteStats,
                getCampaignDetails,
                getCampaignScanList,
                stopCampaign,
                checkCampaign,
                createCampaignUser,
                submitCampaignScan,
                logScanAttempt,
                restoreCampaignScanResult,
                exportCampaignScan,
                saveGuestUser,
                getGuestUserList,
                getGuestUserDetails,
                guestList,
                getUserScanLimit,
                scanLimit,
                setScanLimit,
                is4Life,
                isBiosenseInitialized,
                biosenseSession,
                setBiosenseSession,
                biosenseState,
                setBiosenseState,
                // initBiosenseSDK,
                bioLicenseKey,
                servings,
                updateUserServings,
                fourLifeuserData,
                setFourLifeUserData,
                fourLifeUserLogin,
                products,
                isProfileUpdated,
                fetchFreshProducts,
                dynamicTypes,
                getRecProductList,
                fullProducts,
                fourLifeSSOLogin,
                productCheckout,
                redirection,
                addUserToChat,
                fourLifeVerifyOTP,
                fourLifeEmailOTP,
                fourLiferesendOTP,
                verifiedsecEmail,
                deleteGuestUser,
                getFourLifeScanHistory,
                fourLifeScanHistory,
                saveBPCalibration,
                getBPCalibration,
                deleteBPCalibration,
                downloadUserScanDetails,
                getCompanyBranding,
                scanIssueModal,
                setScanIssueModal,
            }}

        >
            {(isLoading || isInitLoading || msgLoader) && (
                <Loader />
            )}
            {!isInitLoading && children}
        </GlobalContext.Provider>
    );
};
